import type { Permission } from "./access";
import type { MoneyInCurrency } from "./currency";

export * from "./access";
export * from "./suppliers";
export * from "./accounting";
export * from "./hr";
export * from "./materials-cost";
export * from "./modules";
export * from "./billing";
export * from "./subscription";
export * from "./tasks";
export * from "./currency";
export * from "./calendar";
export * from "./workflows";
export * from "./help";

/**
 * Public surface of `@dentacore/shared-types`.
 *
 * The contract between `apps/api` and `apps/web`, defined once so it cannot
 * drift. Structural things live here — roles, response shapes, the header the
 * active branch travels in. **Clinic data never does**: branches, chairs,
 * hours, procedures and prices are runtime rows an admin creates. See
 * CLAUDE.md, *Data is configured, not coded*.
 */

/**
 * The one runtime value in this package, and the reason it has a build step.
 * A `const` array rather than a TypeScript `enum`: enums are awkward under
 * `isolatedModules` and this is also the list a `<select>` renders.
 */
export const ROLES = ["ADMIN", "DENTIST", "HYGIENIST", "ASSISTANT", "FRONT_DESK"] as const;
export type RoleName = (typeof ROLES)[number];

/** Where the active branch travels. Never a body field, never a query parameter. */
export const BRANCH_HEADER = "x-branch-id";

/**
 * Which clinic the browser is signing in to, from its own subdomain.
 *
 * **This is not an authorization input, and the difference from `BRANCH_HEADER`
 * matters.** The branch header says *what the caller may reach* and is checked
 * against their memberships, so trusting the client there would be the whole
 * vulnerability. This one only says *which tenant's users to check the password
 * against* — a wrong value finds no matching user and fails. Once signed in,
 * the clinic comes from the token and never from here.
 */
export const CLINIC_HEADER = "x-clinic-slug";

/**
 * The error code a sign-in returns when it cannot tell which practice is meant.
 *
 * A 400 alone is not enough for the client to act on — it would have to match
 * the message text, which breaks the moment anybody rewords it. With a code the
 * sign-in form knows to reveal its practice field and ask, instead of showing a
 * sentence the reader can do nothing about.
 *
 * Note what it is *not* used for: an unknown practice address still comes back
 * as an ordinary rejected sign-in, because telling the difference would let
 * anyone enumerate the platform's customers.
 */
export const CLINIC_REQUIRED = "CLINIC_REQUIRED";

/**
 * This address works at several practices, and here they are (QA20-1).
 *
 * **Only ever sent after the password has been checked.** The list names
 * clinics, so answering this way on a wrong password would turn sign-in into
 * *"type an address, learn where that person works"* — which for a dental
 * practice discloses something about patients' clinicians as much as about
 * staff. `AuthService.offerTheChoice` is where that ordering lives; nothing
 * else may throw this code.
 *
 * It replaces `CLINIC_REQUIRED` as the thing people actually meet. That one
 * stays for the case where the list cannot be built.
 */
export const CLINIC_CHOICE = "CLINIC_CHOICE";

/** One practice in the sign-in chooser. */
export interface ClinicChoice {
  slug: string;
  name: string;
  /** Where to send the browser. Derived server-side, never from the current page. */
  url: string;
}

/**
 * A patient's phone number is already on another file (decision D2).
 *
 * The form uses this to raise the "add them anyway?" confirm rather than to
 * print a sentence — the reader has two real options, opening the existing file
 * or declaring a shared household line, and neither is discoverable from prose
 * alone.
 *
 * **It was a checkbox until Anthony's QA (item 18)**, revealed on refusal and
 * ticked before a second save. Same guard and same only-on-collision
 * appearance; one save instead of two. The reply carries `patientName` and
 * `fileNumber` so the dialog can name who already has the number.
 */
export const PHONE_ALREADY_USED = "PHONE_ALREADY_USED";

/**
 * A plan's visit cannot be acted on because an earlier visit is unfinished
 * (TP8, refined by Anthony's QA item 6).
 *
 * **TP8 itself is unchanged** — visits are booked, linked and charted strictly
 * in order. What changed is that the refusal used to be a sentence and a dead
 * end: the tester had two appointments already in the diary and could not chart
 * the second because nobody had marked the first complete, which is an
 * oversight rather than a clinical decision.
 *
 * So the reply carries the outstanding visit as data — `outstandingPartId` and
 * `outstandingVisitNumber` — and the screen offers to complete it from the
 * refusal itself.
 */
export const PLAN_VISIT_OUT_OF_ORDER = "PLAN_VISIT_OUT_OF_ORDER";

/**
 * A visit booked on a date before the visit ahead of it, or after the one
 * behind it (CH8). Carries `otherVisitNumber` and `otherStartsAt`, so a screen
 * can say which booking is in the way without parsing the sentence.
 */
export const PLAN_VISIT_DATE_ORDER = "PLAN_VISIT_DATE_ORDER";

/**
 * An email address already has an account somewhere on the platform (D1).
 *
 * Deliberately says no more than that. The account may be at another clinic,
 * which this one has no business learning anything else about — and the person
 * adding staff can only act on it in one way regardless: use a different
 * address.
 */
export const EMAIL_ALREADY_USED = "EMAIL_ALREADY_USED";

/** Correlation id, echoed if the caller supplies one and minted otherwise. */
export const REQUEST_ID_HEADER = "x-request-id";

// ── auth ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  clinicId: string;
  /** The clinic's own name, for the sidebar. Not a constant anywhere. */
  clinicName: string;
  /**
   * ISO 4217, from `clinics.currency` — **the home currency** (QA4-D6): what
   * salaries, shared expenses and supplier prices are in. A location's prices
   * are in its own `Branch.currency`; read that for anything at a location.
   * Every price is integer minor units and the currency says how many make a
   * unit — JPY has none, KWD has three.
   */
  currency: string;
  /**
   * True when this clinic is the labelled fixture `pnpm db:seed` creates. The
   * app shows an unmissable banner while it is, so demo data is never mistaken
   * for a real patient list.
   */
  isDemoClinic: boolean;
  /**
   * Still holding a password an operator chose (decision SA1). While true the
   * application refuses to go anywhere but the change-password screen — and the
   * API refuses too, which is the control; this is what stops the reader seeing
   * a page full of refusals instead.
   */
  mustChangePassword: boolean;
  /**
   * ISO-3166 alpha-2, or null when the clinic hasn't set one (`clinics.default_country`).
   * Prefills a phone country picker only — a null here means the picker starts
   * unselected, never a guessed country.
   */
  clinicDefaultCountry: string | null;
  email: string;
  firstName: string;
  lastName: string;
  /** What they are clinically. Not what they may reach — see canAdminister. */
  role: string;
  /**
   * Whether they may reach the admin-only screens.
   *
   * Separate from role since 2026-09-07, so one person can be both — the owner
   * of a single-dentist practice holds DENTIST and true.
   *
   * **Since STORY-160 this is the Administrator role** (RB9): holding it means
   * holding every permission. Ask `permissions` for anything else.
   */
  canAdminister: boolean;
  /**
   * Every permission they hold right now, worked out on the server from their
   * roles and overrides (STORY-162). The screen reads it to decide what to
   * draw; **the API refuses on its own whatever this says**, so a stale copy
   * can only ever show a button that then explains why it is refused.
   */
  permissions: Permission[];
}

export interface TokenPair {
  accessToken: string;
  /** Single use. Replaying one after it is rotated revokes the whole session. */
  refreshToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
  user: AuthUser;
}

/** What `POST /auth/refresh` returns — the same, without the user. */
export type RefreshedTokens = Omit<TokenPair, "user">;

// ── the clinic's structure ──────────────────────────────────────────────────

export interface Branch {
  id: string;
  name: string;
  timezone: string;
  /** ISO 4217 — this location's own currency (QA4-D6). */
  currency: string;
  address: string | null;
  /**
   * ISO 3166-1 alpha-2, or null when this location has never been given one
   * (decision D4).
   *
   * **Its job is the dialling code**, not the address: a phone field recorded
   * at this location prefills from here, falling back to the clinic's own
   * `defaultCountry` and then to an unselected picker. A practice with a branch
   * either side of a border used to get the tenant's country on both.
   */
  country: string | null;
  phoneE164: string | null;
  /**
   * Where this location actually is, in decimal degrees — **read out of
   * `mapsUrl` rather than typed** since 2026-09-20.
   *
   * **Its one reader is the WhatsApp appointment reminder**, whose template
   * declares a LOCATION header — Meta refuses the send without latitude,
   * longitude, name and address. There is no map in the product and no
   * distance is computed from these.
   *
   * **Both or neither**, enforced by the database: half a pin is a send that
   * fails at the moment a patient was due to be reminded. Nothing can write
   * half of one any more, because the one thing an admin supplies is a link.
   */
  latitude: number | null;
  longitude: number | null;
  /**
   * The Google Maps link those two were read from, and what the settings
   * screen shows back.
   *
   * **Null for a branch located before links existed**, whose coordinates were
   * typed in directly. That is not a missing location — the pair above is what
   * the reminder reads, and it is intact.
   */
  mapsUrl: string | null;
  isActive: boolean;
  /** Automatic evening-before WhatsApp reminders are on here (EB5). Absent without Chatbot. */
  autoRemindersEnabled?: boolean;
  /** The hour they go out, 0–23 on this location's own clock (EB5). Absent without Chatbot. */
  autoReminderHour?: number;
  /** Minutes since midnight the day grid starts at, a whole hour (STORY-177). */
  calendarOpensAt: number;
  /** Minutes since midnight it ends at, a whole hour after `calendarOpensAt`. */
  calendarClosesAt: number;
  /** The calendar's scale as a percent of the original, 40–150. */
  calendarZoom: number;
  /**
   * Whether **the caller** holds a membership of this branch, and so whether
   * the branch-scoped routes will admit them to it.
   *
   * An admin is listed every branch in their clinic but `BranchScopeGuard`
   * admits on `branch_memberships`, not on role — so a screen that offers a
   * branch without reading this offers one the very next request refuses.
   */
  isMember: boolean;
}

/** `PATCH /branches/:id`. Every field optional — send only what changed. */
export interface UpdateBranch {
  name?: string;
  timezone?: string;
  country?: string;
  address?: string;
  phoneE164?: string;
  /**
   * A Google Maps link. The server reads its coordinates out and stores both.
   * **`null` clears the location; omitting it leaves the stored one alone.**
   */
  mapsUrl?: string | null;
  isActive?: boolean;
  autoRemindersEnabled?: boolean;
  autoReminderHour?: number;
  calendarOpensAt?: number;
  calendarClosesAt?: number;
  calendarZoom?: number;
  /**
   * A new currency for this location (QA4-D6). Its prices, drafts and unbilled
   * plan steps convert at `exchangeRate` in the same save.
   */
  currency?: string;
  /** "1 old = X new", as typed — a string because a rate is exact. */
  exchangeRate?: string;
}

/** `POST /branches`. Admin only. */
export interface CreateBranch {
  name: string;
  timezone: string;
  address?: string;
  phoneE164?: string;
  /** A Google Maps link; its coordinates are read out and stored. */
  mapsUrl?: string | null;
}

export interface Chair {
  id: string;
  branchId: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  /**
   * Minutes this chair needs after an appointment, copied onto each booking
   * made into it. Always read from here, never from a literal — a clinic that
   * wants 10 changes one row.
   *
   * **Per chair since 2026-09-23**, because turnover is cleaning and setup
   * time for a room: a surgery and a hygiene chair do not clean down at the
   * same speed, and the branch-wide number forced the slower of the two onto
   * both. Reversing this would silently repad every short appointment.
   */
  defaultTurnoverMinutes: number;
  /**
   * Every appointment ever booked into this chair, any status. Drives the
   * "are you sure" confirm before retiring — a long history deserves one.
   */
  appointmentCount: number;
  /**
   * Of those, the ones starting after now. **Retiring is refused outright
   * while this is above zero** (decision S1) — future bookings in a retired
   * chair are a scheduling error, not a history worth confirming.
   */
  futureAppointmentCount: number;
}

/** `POST /chairs`. Omit the turnover to take the column's default. */
export interface CreateChair {
  name: string;
  sortOrder?: number;
  defaultTurnoverMinutes?: number;
}

/** `PATCH /chairs/:id`. Setting `isActive: false` is how a chair is retired. */
export interface UpdateChair {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  /**
   * **Takes effect on the next booking, and reaches nothing already on the
   * diary** (2026-09-23). Each appointment keeps the turnover it was booked
   * with — see `Appointment.turnoverMinutes`.
   */
  defaultTurnoverMinutes?: number;
}

// ── block reasons ───────────────────────────────────────────────────────────
//
// STORY-046c, decisions G12 and G25. Why a chair is held when nobody is in it.

/**
 * One reason a chair can be held, as this branch defines it.
 *
 * **The label is the only thing that reaches Google.** A buffer block has no
 * patient, so D3's "initials and procedure" formatter has neither to offer, and
 * taking the label from a clinical field verbatim would eventually send
 * *"Mrs Haddad — crown prep, allergic to latex"* out of the building (G12).
 *
 * **Rows, not an enum** (G25, answering Q27 on 2026-09-04 and reversing the
 * grill's fixed-list default): a chosen set closes that leak whether it is fixed
 * or configured, so the enum bought nothing and cost *data is configured, not
 * coded*. A practice that wanted "Lab pickup" would have picked "Other", and
 * Google would have shown "Other" for ever.
 *
 * Branch-scoped like the procedure catalogue beside it.
 */
export interface BlockReason {
  id: string;
  branchId: string;
  label: string;
  /** Retired reasons leave the picker; every block already held keeps reading. */
  isActive: boolean;
  sortOrder: number;
}

export interface CreateBlockReason {
  label: string;
}

/** `PATCH /block-reasons/:id`. `isActive: false` retires it — it is not a delete. */
export interface UpdateBlockReason {
  label?: string;
  isActive?: boolean;
  sortOrder?: number;
}

// ── inventory ───────────────────────────────────────────────────────────────
//
// STORY-108/109. **Counts are in *uses* throughout**: `quantityOnHand`,
// `parLevel` and a material's quantity are all counts of uses, and
// `usesPerStockUnit` says only how many uses a purchased pack yields — a tube
// is 1, because a tube is one thing you open.

/** What an item *is*, and it decides behaviour rather than labelling it (TP7). */
export type InventoryKind =
  /** One use is one unit. The only kind that deducts automatically. */
  | "COUNTABLE"
  /** Held as whole units; moves only when somebody marks one opened. */
  | "BULK"
  /** Appears on the prep list and **never depletes**. */
  | "REUSABLE";

/** RESTOCK is a supplier order arriving (STORY-171). */
export type InventoryMovementKind = "CONSUMPTION" | "ADJUSTMENT" | "OPENED" | "RESTOCK";

export interface InventoryItem {
  id: string;
  branchId: string;
  name: string;
  kind: InventoryKind;
  /** What a pack is called — "box of 20", "tube". Free text the clinic types. */
  stockUnit: string;
  usesPerStockUnit: number;
  /** What one pack costs, in minor units. Always set (IP-D1). */
  priceCents: number;
  /** No price yet, for this location to set (OQ-D7); `priceCents` is 0 until then. */
  priceNeeded: boolean;
  /**
   * About how many uses one pack serves — **bulk items only, and always set on
   * them** (MC-D4). For costing a procedure; no count or low line reads it.
   */
  estimatedUses: number | null;
  /**
   * In uses. **May be negative** (decision TP14) — stock refuses nothing, so a
   * figure below zero means the count is stale rather than that somebody should
   * have been stopped. Render it, do not clamp it.
   */
  quantityOnHand: number;
  /** In uses. Null means the clinic has not said, and no notice is raised. */
  parLevel: number | null;
  /** In uses — at or below this the item is low (IC2). Always set (IC7). */
  lowAt: number;
  isActive: boolean;
  /**
   * How many of this branch's procedures list this material. The screen warns
   * with it before retiring — the `Chair.futureAppointmentCount` idiom, except
   * that this one never refuses.
   */
  usedByProcedureCount: number;
  /**
   * In uses — **what live bookings will take** (FX26): every future booking
   * whose procedure lists this item, plus any visit under way. Worked out on
   * read, so it is always the diary's figure. Countable items only; zero for
   * the rest.
   */
  bookedQuantity: number;
  /**
   * Somebody marked it to buy (WB-D1): its own flag, not the cart. Starred on
   * the shelf and at the top of the catalogue until an order for it is placed.
   */
  wantToBuy: boolean;
  /** An order for it is on its way (MOD-D24) — shown as "Ordered" beside a low item. */
  onOrder: boolean;
}

export interface CreateInventoryItem {
  name: string;
  kind: InventoryKind;
  stockUnit: string;
  usesPerStockUnit: number;
  /** Required, above zero (IP-D1). */
  priceCents: number;
  /** Required for a bulk item and refused on any other kind (MC-D4). */
  estimatedUses?: number;
  quantityOnHand?: number;
  parLevel?: number;
  /** Required (IC7): at or below this many uses the item is low. */
  lowAt: number;
}

/**
 * `PATCH /inventory/:id`. Setting `isActive: false` retires it.
 *
 * **`quantityOnHand` is deliberately absent**: only a movement changes a count,
 * so the ledger can always say why a figure moved. The server refuses the
 * property outright rather than ignoring it.
 */
export interface UpdateInventoryItem {
  name?: string;
  kind?: InventoryKind;
  stockUnit?: string;
  usesPerStockUnit?: number;
  /** Can be changed, never cleared (IP-D1). */
  priceCents?: number;
  /**
   * Bulk items only (MC-D4). Changing an item *to* bulk needs one; changing it
   * away from bulk clears it.
   */
  estimatedUses?: number;
  parLevel?: number | null;
  /** Can be changed, never cleared (IC7). */
  lowAt?: number;
  isActive?: boolean;
}

export interface AdjustInventory {
  /** Signed, in uses. Zero is refused. */
  quantityDelta: number;
  /**
   * Optional since IC5: *Add* and *Remove* on the stock list ask for a number
   * and nothing else. A correction that has a reason still records it.
   */
  reason?: string;
}

/**
 * Where an item made **without a person typing a threshold** starts — from a
 * supplier's price list or the supplier import (IC7): a quarter of its full
 * shelf, or of one pack. Editable on the item at once. It is STORY-112's old
 * rule, kept only as a starting value; nothing reads it at runtime.
 */
export const LOW_STOCK_SHARE = 0.25;

export function startingLowAt(item: { parLevel: number | null; usesPerStockUnit: number }): number {
  return Math.floor((item.parLevel ?? item.usesPerStockUnit) * LOW_STOCK_SHARE);
}

/**
 * The count at or below which an item is low, in uses — **its own `lowAt`**,
 * which every item carries since IC7.
 *
 * **One copy for the screen and the server's notice**, so the warm dot on the
 * list and the Home alert can never disagree about the same item.
 */
export function lowLine(item: { lowAt: number }): number {
  return item.lowAt;
}

/**
 * What is on the shelf and not already promised to a booking (FX26). Can be
 * negative — that is how many are missing for what is booked.
 */
export function availableQuantity(item: {
  quantityOnHand: number;
  bookedQuantity?: number;
}): number {
  return item.quantityOnHand - (item.bookedQuantity ?? 0);
}

/**
 * Is this item low? **A reusable item never is** — it is never used up (TP7) —
 * and **a retired one is not either**, because nobody is going to reorder it.
 */
export function isRunningLow(item: {
  kind: InventoryKind;
  isActive: boolean;
  quantityOnHand: number;
  lowAt: number;
  /** Booked for procedures (FX26). Absent counts as none. */
  bookedQuantity?: number;
}): boolean {
  if (!item.isActive || item.kind === "REUSABLE") return false;
  // **Judged on what is left after the bookings** (FX26): four crowns with
  // three booked is one crown, and that is what the alert is about.
  return availableQuantity(item) <= lowLine(item);
}

/**
 * Whether a material scales with the teeth treated (decision TP15).
 *
 * `PER_VISIT` means the whole appointment (TP16) — knowingly slightly wrong for
 * a multi-quadrant visit, and affordable only because stock refuses nothing.
 */
export type MaterialScope = "PER_VISIT" | "PER_TOOTH";

/** One line of a procedure's bill of materials. Quantities are in uses. */
export interface ProcedureMaterial {
  id: string;
  procedureCodeId: string;
  inventoryItemId: string;
  itemName: string;
  itemKind: InventoryKind;
  /**
   * False once the item was retired after this line was written. The line still
   * shows, so it can be seen and removed — hiding it would leave a bill nobody
   * could correct.
   */
  itemIsActive: boolean;
  /** In uses, before `scope` multiplies it. */
  quantity: number;
  scope: MaterialScope;
  notes: string | null;
  /**
   * The item's price per pack, so the line can be costed (STORY-175a). **The
   * server sends this and the next two only with Accounting and
   * `accounting.view`** (QF-D1); cost a tray through `costedLines`, which says
   * when they are missing.
   */
  itemPriceCents?: number;
  itemUsesPerStockUnit?: number;
  /** Bulk items only (MC-D4). */
  itemEstimatedUses?: number | null;
}

export interface AddProcedureMaterial {
  inventoryItemId: string;
  quantity: number;
  scope: MaterialScope;
  notes?: string;
}

/** The same without the item — which line it is comes from the path. */
export interface EditProcedureMaterial {
  quantity: number;
  scope: MaterialScope;
  notes?: string;
}

/**
 * What to put on the tray for one booking — STORY-110c.
 *
 * **Per-tooth quantities are reported, not multiplied.** How many teeth a visit
 * will treat is not known when the tray is set, so the list says "2 per tooth"
 * rather than pretending to a number. STORY-111 multiplies at completion.
 */
export interface PrepList {
  appointmentId: string;
  /** Null when the reason was typed rather than picked — a normal state. */
  procedureCodeId: string | null;
  procedureLabel: string | null;
  /** Empty when there is no procedure, or the procedure lists no materials. */
  materials: ProcedureMaterial[];

  /*
   * Which visit of which plan this booking fulfils, when it fulfils one.
   *
   * **All null for an ordinary booking**, which is most of them. A booking that
   * reads only "Crown preparation" leaves whoever opens it working out whether
   * this is the prep or the cementation; these answer that from the appointment.
   */
  planId: string | null;
  planTitle: string | null;
  visitNumber: number | null;
  visitCount: number | null;
}

export interface InventoryMovement {
  id: string;
  inventoryItemId: string;
  kind: InventoryMovementKind;
  quantityDelta: number;
  appointmentId: string | null;
  reason: string | null;
  /** Null once that person has left the practice — the movement survives them. */
  createdByName: string | null;
  createdAt: string;
}

// ── patients ────────────────────────────────────────────────────────────────

/** The two values `sex` may take. Nullable everywhere — see the schema comment. */
export const SEXES = ["MALE", "FEMALE"] as const;
export type SexName = (typeof SEXES)[number];

export interface Patient {
  id: string;
  /** The plain integer, for sorting and searching. */
  fileNumber: number;
  /**
   * Zero-padded for display, e.g. `#00001`. The API formats it, so the web app
   * and any future desktop build cannot disagree about the padding.
   */
  fileNumberDisplay: string;
  firstName: string;
  lastName: string;
  phoneE164: string;
  /** ISO date. */
  dateOfBirth: string | null;
  /**
   * Derived from `dateOfBirth` on every read and **never stored** — a stored
   * age is wrong the day after a birthday.
   */
  age: number | null;
  sex: SexName | null;
  email: string | null;
  address: string | null;
  insurerName: string | null;
  policyReference: string | null;
  /** The usual branch, for defaults and reporting. A patient is not owned by one. */
  homeBranchId: string | null;
  /** ISO date the next routine check-up is due. */
  recallDueOn: string | null;
  /**
   * Where this file came from. `FRONT_DESK` is one a member of staff typed in;
   * `WHATSAPP` is one the assistant opened from what a caller said about
   * themselves, which nobody at the practice has checked. The patient header
   * renders the second as a "Chatbot" chip.
   *
   * A plain string, as an appointment's own `source` already crosses the wire.
   */
  source: string;
  createdAt: string;
  updatedAt: string;
  /**
   * STORY-122 (SA3) — set only by `create`/`update`, never by an ordinary
   * read. Non-null when this number is also a staff member's WhatsApp
   * assistant number. A signal, not a guard: the save already happened.
   */
  staffCollision?: { userId: string; name: string } | null;
  /**
   * Set only by `create`/`update` (PP-D15): the number saved, but is not valid
   * for its country — a warning to show, never a refusal.
   */
  phoneWarning?: string | null;
}

/**
 * What it takes to open a file. Only `firstName`, `lastName` and `phoneE164`
 * are required — everything else is filled in at the first examination.
 * `fileNumber` is never part of this: it is server-issued.
 */
export interface CreatePatientRequest {
  firstName: string;
  lastName: string;
  phoneE164: string;
  dateOfBirth?: string;
  sex?: SexName;
  email?: string;
  address?: string;
  insurerName?: string;
  policyReference?: string;
  homeBranchId?: string;
  recallDueOn?: string;
}

/**
 * Any subset of a patient's editable fields. An **omitted** field is left
 * untouched; an explicit **`null`** clears it — dates included since QF-D37,
 * which stopped a cleared date being stored as 1 January 1970. A null on a field
 * that cannot be empty (a name, the phone) is refused with a 400.
 */
export interface UpdatePatientRequest {
  firstName?: string;
  lastName?: string;
  phoneE164?: string;
  dateOfBirth?: string | null;
  sex?: SexName | null;
  email?: string | null;
  address?: string | null;
  insurerName?: string | null;
  policyReference?: string | null;
  homeBranchId?: string | null;
  recallDueOn?: string | null;
  /** Shares a household number (D2) — saved on an edit since STORY-206. */
  sharesHouseholdNumber?: boolean;
  /**
   * The values the form opened with (PP-D12): a field changed here that somebody
   * else changed since is refused `STALE_FIELDS`; one left alone is not written.
   */
  expected?: Record<string, unknown>;
}

/**
 * How `GET /patients` orders its results when a sort is explicitly chosen.
 *
 * Omitted, ordering is untouched — best match first while searching, newest
 * file number first while browsing. Chosen, it replaces that entirely rather
 * than sitting under it.
 */
export const PATIENT_SORTS = ["name", "fileNumber", "recent", "attendance"] as const;
export type PatientSort = (typeof PATIENT_SORTS)[number];

/**
 * A patient in a search result, plus how reliably they turn up.
 *
 * **Separate from `Patient`** (Anthony's QA, item 19), because only the search
 * computes these. Putting them on `Patient` would promise them everywhere a
 * patient is returned — the file header, the appointment payload, the plan
 * screen — and every one of those would then read `undefined`.
 */
export interface PatientSearchResult extends Patient {
  /** Appointments they attended. */
  attendedCount: number;
  /** Appointments they did not turn up for. A cancellation is not one of these. */
  noShowCount: number;
  /**
   * `attendedCount ÷ (attendedCount + noShowCount)`, or **null with no
   * concluded history** — a new patient has earned neither a good score nor a
   * bad one, so the screen says "no history" rather than 0% or 100%.
   */
  attendanceRate: number | null;
}

/** One page of `GET /patients` — the search box and the browse list at once. */
export interface PatientPage {
  patients: PatientSearchResult[];
  /**
   * Opaque. Pass back as `cursor` for the next page; null at the end.
   *
   * Cursor rather than offset so a patient created mid-scroll cannot make a row
   * appear twice or vanish, which is exactly what a busy front desk does.
   */
  nextCursor: string | null;
  /** Every patient the query matches, across all pages — for numbered pages (PF4). */
  total: number;
}

// ── patient flags ───────────────────────────────────────────────────────────

export const FLAG_KINDS = ["ALLERGY", "MEDICAL", "MEDICATION", "BEHAVIOURAL", "ADMIN"] as const;
export type FlagKindName = (typeof FLAG_KINDS)[number];

/**
 * CRITICAL renders loud, sorts first, and travels with the appointment payload.
 * The order of this list is the render order — it matches the database enum's
 * declaration order, which is what Postgres sorts by.
 */
export const FLAG_SEVERITIES = ["CRITICAL", "INFO"] as const;
export type FlagSeverityName = (typeof FLAG_SEVERITIES)[number];

/** The compact form that travels outside the patient's own page. */
export interface PatientFlagSummary {
  id: string;
  kind: FlagKindName;
  label: string;
  detail: string | null;
  severity: FlagSeverityName;
}

export interface PatientFlag extends PatientFlagSummary {
  /** False once resolved. Resolved flags are kept, never deleted. */
  isActive: boolean;
  resolvedAt: string | null;
  createdAt: string;
  /**
   * What it said before each edit, newest first (PP-D11). On the list read
   * only; absent on a write's reply.
   */
  history?: {
    label: string | null;
    detail: string | null;
    severity: FlagSeverityName | null;
    changedAt: string;
    changedBy: string | null;
  }[];
}

/** One patient file, with the flags its header renders. */
export interface PatientFile extends Patient {
  /** Active flags, critical first. */
  flags: PatientFlagSummary[];
  hasCriticalFlag: boolean;
}

/**
 * One patient file, as the header renders it.
 *
 * Extends `PatientFile` with the At-a-glance figures — kept off the search
 * result deliberately, because a list of a hundred rows should not carry a
 * balance query each.
 */
export interface PatientFileWithGlance extends PatientFile {
  /**
   * Outstanding balance in **integer minor units, one entry per currency**
   * (QA4-D6) — never one total across them. Negative means in credit. Empty
   * when nothing was billed: show a zero in `currency`, the home currency.
   *
   * **Null for anyone without `billing.view`** (STORY-170): what a patient owes
   * is billing, and the patient file is readable with patients.view alone.
   */
  outstanding: MoneyInCurrency[] | null;
  /** Draft invoice totals per currency (PP-D8). Null without billing.view. */
  drafted: MoneyInCurrency[] | null;
  /** The clinic's home currency. */
  currency: string;
  /** ISO timestamp of the next booked appointment, or null. */
  nextAppointmentAt: string | null;
}

// ── the tooth chart ─────────────────────────────────────────────────────────

/**
 * The four **stored** states (working plan §6.3).
 *
 * A tooth's state is **derived from the procedures on it** — recording work
 * makes it TREATED, deleting the last one returns it to SOUND. PLANNED arrives
 * from a treatment plan and the chart editor never sets it.
 *
 * **This is not what the chart draws** — see `CHART_TOOTH_STATES`. This type
 * survives for `ToothDetail`, which reports the stored value.
 */
export const TOOTH_STATES = ["SOUND", "TREATED", "PLANNED", "MISSING"] as const;
export type ToothStateName = (typeof TOOTH_STATES)[number];

/**
 * What the chart draws (BF5, 2026-09-24) — **the same for everyone**.
 *
 * `DONE` is the stored `TREATED`, blue whoever did the work: UI-TC7's split
 * into *done by me* and *done by others* was reversed. `TO_BE_DONE` is red —
 * charted planned work, an unstarted plan step, or a live booking on the tooth.
 */
export const CHART_TOOTH_STATES = ["SOUND", "TO_BE_DONE", "DONE", "MISSING"] as const;
export type ChartToothStateName = (typeof CHART_TOOTH_STATES)[number];

/** Additive marks on a tooth (UI-TC10). Never a state — a glyph decides no colour. */
export const TOOTH_GLYPHS = ["CROWN", "ROOT_CANAL"] as const;
export type ToothGlyphName = (typeof TOOTH_GLYPHS)[number];

export interface ToothRecord {
  /** FDI. 11–18, 21–28, 31–38, 41–48, or a baby tooth 51–55, 61–65, 71–75, 81–85 (BT4). */
  fdiNumber: number;
  state: ChartToothStateName;
  glyphs: ToothGlyphName[];
  /** The parts completed work touched, from M, O, D, B and L — the chart's top view. */
  surfacesWorked: string[];
  /** The parts planned work names, from M, O, D, B and L — red on the top view. */
  surfacesPlanned: string[];
  updatedAt: string;
}

/**
 * A patient's charted teeth. **Only teeth with a record appear** — an
 * unrecorded tooth is drawn sound, because it is the absence of a record
 * rather than a record of absence.
 */
export interface ToothChart {
  /**
   * Whether the chart shows the baby-teeth layer (BT1–BT3): under 13 unless
   * switched off, 13 or older only if switched on after the 13th birthday.
   */
  primaryTeeth: boolean;
  teeth: ToothRecord[];
}

export const TOOTH_PROCEDURE_STATUSES = ["PLANNED", "COMPLETED", "CANCELLED"] as const;
export type ToothProcedureStatusName = (typeof TOOTH_PROCEDURE_STATUSES)[number];

/**
 * One charted procedure, as the patient's *history* reads it — across every
 * tooth rather than inside one.
 *
 * The chart answers "what is in this mouth now". This answers "what have we
 * done for this person", which is chronological, crosses teeth, and needs the
 * two things a tooth's own history does not: which tooth, and who did it.
 */
export interface PatientProcedure {
  id: string;
  /** Null is whole-mouth work, a scale and polish (EB6). Never on the chart. */
  fdiNumber: number | null;
  /** "Whole mouth" when `fdiNumber` is null. */
  toothName: string;
  label: string;
  surfaces: string[];
  status: ToothProcedureStatusName;
  performedAt: string | null;
  /** By name. Null where the clinician's account has since been removed. */
  performedBy: string | null;
  /** The visit it was charted at, so an entry can be traced to its invoice. */
  appointmentId: string | null;
  visitDate: string | null;
  createdAt: string;
}

export interface ToothProcedure {
  id: string;
  fdiNumber: number;
  label: string;
  /**
   * Provenance, not a dependency. The catalogue is per branch (ADR-0008), so a
   * procedure charted at one branch may point at a row another branch does not
   * have — `label` is what keeps the record readable regardless.
   */
  procedureCodeId: string | null;
  surfaces: string[];
  status: ToothProcedureStatusName;
  performedAt: string | null;
  performedById: string | null;
  createdAt: string;
}

/** One tooth: its state, and every procedure ever charted on it, newest first. */
/**
 * A step a live treatment plan intends for this tooth (decision D7).
 *
 * **Read from the plan, never copied onto the chart.** Writing a `PLANNED`
 * tooth procedure when a plan step is added would put the same fact in two
 * tables and guarantee they drift the first time a plan is edited in place —
 * which TP22 says is the normal way a plan changes.
 */
export interface PlannedToothWork {
  planItemId: string;
  planId: string;
  planTitle: string | null;
  /** One-based, as every other screen counts visits. */
  visitNumber: number;
  visitTitle: string | null;
  label: string | null;
  status: "PLANNED" | "SCHEDULED" | "IN_PROGRESS" | "CANCELLED";
}

export interface ToothDetail {
  fdiNumber: number;
  state: ToothStateName;
  /** `Upper right first molar` — what a clinician says out loud. */
  name: string;
  procedures: ToothProcedure[];
  /** What a live plan intends for this tooth, in visit order (decision D7). */
  planned: PlannedToothWork[];
  /** Bookings on this tooth still to happen (BF3) — what makes it red. */
  booked: BookedToothWork[];
}

/** A booking on a tooth still to happen (BF3). */
export interface BookedToothWork {
  appointmentId: string;
  startsAt: string;
  label: string | null;
}

/**
 * **Whether a procedure is done on a tooth** (TP12), deciding exactly one
 * thing: whether a treatment plan asks which tooth. Whitening must not be
 * asked; a composite, crown or root canal must.
 *
 * **Not a clinical taxonomy, and it must not become one.** Two values, one
 * question — anything richer belongs in the procedure itself.
 */
export type ProcedureScope = "PER_TOOTH" | "WHOLE_MOUTH";

/** One row of a branch's own catalogue. Ships empty; STORY-068 fills it. */
/**
 * The colours a procedure category may take — **our palette, never a clinic's
 * hex** (PG-D3). Each key is a token pair in `tokens.css` (`--c-cat-<key>`),
 * measured against every calendar block in both themes. Mirrors the CHECK on
 * `procedure_categories.colour`; adding one is a token, this list and that CHECK.
 */
export const PROCEDURE_CATEGORY_COLOURS = ["red", "blue", "yellow", "black", "green"] as const;
/**
 * Colours the first palette offered (2026-10-06), still valid on rows that
 * already hold them and still drawn, but no longer offered or accepted on a new
 * write. They were too close together to tell apart on the grid — Anthony,
 * 2026-10-11 — so the offered palette is five far-apart hues.
 */
export const LEGACY_PROCEDURE_CATEGORY_COLOURS = [
  "indigo",
  "violet",
  "magenta",
  "amber",
  "olive",
  "slate",
] as const;
export type ProcedureCategoryColour =
  (typeof PROCEDURE_CATEGORY_COLOURS)[number] | (typeof LEGACY_PROCEDURE_CATEGORY_COLOURS)[number];

/**
 * One of a clinic's own procedure categories (STORY-176a, PG-D1). One list per
 * clinic, shared by its locations (PG-D2); every procedure belongs to one
 * (PG-D4), and its colour marks that procedure's bookings on the calendar.
 */
export interface ProcedureCategory {
  id: string;
  name: string;
  colour: ProcedureCategoryColour;
  sortOrder: number;
  /** Procedures in it, every location, retired ones included — removal is refused above zero (PG-D5). */
  procedureCount: number;
}

export interface CreateProcedureCategory {
  name: string;
  colour: ProcedureCategoryColour;
}

export type UpdateProcedureCategory = Partial<CreateProcedureCategory>;

export interface ProcedureCode {
  id: string;
  branchId: string;
  code: string;
  label: string;
  /** Older free text, hidden since STORY-152; the tooth chart's glyphs read it. */
  category: string | null;
  /** Which of the clinic's categories (PG-D4). Always set. */
  procedureCategoryId: string;
  defaultDurationMinutes: number;
  /** Integer minor units. The exponent follows `clinics.currency`. */
  defaultPriceCents: number;
  /** Defaults to `PER_TOOTH`, because most dentistry is. */
  scope: ProcedureScope;
  /** Done where a tooth is missing — an implant, a pontic (PP-D4). */
  doneOnMissingTooth: boolean;
  isActive: boolean;
  /**
   * The visits it takes, in order (PV1). **Empty means one visit**, read from
   * this row alone. With two or more, `defaultPriceCents` is their sum and
   * `defaultDurationMinutes` the first one's length (PV2).
   */
  visits: ProcedureVisit[];
}

/** One visit of a multi-visit procedure (PV1). */
export interface ProcedureVisit {
  id: string;
  /** 0-based position. */
  sortOrder: number;
  title: string | null;
  /** Days after the previous visit. Null on the first. */
  gapDays: number | null;
  /** What completing this visit bills (PV4). Integer minor units. */
  priceCents: number;
  durationMinutes: number;
}

/** One visit as an admin writes it — the whole list is sent at once. */
export interface ProcedureVisitInput {
  title?: string;
  gapDays?: number | null;
  priceCents: number;
  durationMinutes: number;
}

/**
 * One clinical note, as the patient file shows it.
 *
 * **This is the head of a revision chain, not a row.** Editing a note inserts a
 * new row and marks the old one superseded (working plan §2.5), so the two
 * timestamps mean different things and both matter:
 *
 *   `writtenAt` — when the FIRST version was written. The clinical date, and
 *                 what the file is ordered by. A note written in March and
 *                 corrected in August is still a March note.
 *   `revisedAt` — when the current version was written, or null if never edited.
 */
export interface ClinicalNote {
  /** The current version's id — what a revision or a removal is addressed to. */
  id: string;
  title: string | null;
  body: string;
  /**
   * `FRONT_DESK`, or `WHATSAPP` for a note the assistant took from the patient.
   *
   * **Read this before `authorName`.** A null author means three different
   * things — the assistant wrote it, it was imported, or the person who wrote
   * it has since been deleted — so "Author no longer on file" is only the right
   * message for the third.
   */
  source: string;
  authorId: string | null;
  authorName: string | null;
  /** Null for a note written outside an appointment. */
  appointmentId: string | null;
  writtenAt: string;
  revisedAt: string | null;
  /** Versions in the chain. 1 means never edited. */
  revisions: number;
  deletedAt: string | null;
  deletedByName: string | null;
}

/** One version out of a note's chain. The original is never overwritten. */
export interface ClinicalNoteVersion {
  id: string;
  title: string | null;
  body: string;
  source: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  isCurrent: boolean;
}

/** What a note write carries. `appointmentId` is create-only. */
export interface WriteClinicalNote {
  title?: string;
  body: string;
  appointmentId?: string;
}

/**
 * What may be uploaded, and how large (P2 Q6).
 *
 * Shared because the client checks it for a fast, kind error message and the
 * **presign request checks it as the actual control** — a client-side allowlist
 * is a convenience, and a browser that skipped it would otherwise be handed a
 * signed URL for anything at all.
 */
export const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "application/pdf"] as const;
export type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * What kind of image or document this is (P2 Q13).
 *
 * **A clinical vocabulary, not a clinic's configuration** — no practice invents
 * its own name for a bitewing — so shipping the list does not breach §3.9. The
 * procedure catalogue is the contrast: every branch owns its own (ADR-0008),
 * because a practice genuinely does decide what it offers and what it charges.
 */
export const ATTACHMENT_KINDS = [
  "PERIAPICAL",
  "BITEWING",
  "PANORAMIC",
  "INTRAORAL_PHOTO",
  "DOCUMENT",
] as const;
export type AttachmentKindName = (typeof ATTACHMENT_KINDS)[number];

/** How each kind reads on screen. */
export const ATTACHMENT_KIND_LABELS: Record<AttachmentKindName, string> = {
  PERIAPICAL: "Periapical",
  BITEWING: "Bitewing",
  PANORAMIC: "Panoramic",
  INTRAORAL_PHOTO: "Intraoral photo",
  DOCUMENT: "Document",
};

/** An image is of one FDI tooth, or of the whole mouth. */
export const FULL_MOUTH = "FULL_MOUTH";

/** One image or document on a patient's file. */
export interface Attachment {
  id: string;
  kind: AttachmentKindName;
  /** `FULL_MOUTH` or an FDI tooth number as a string. */
  region: string | null;
  mimeType: string;
  sizeBytes: number;
  /** Lower-case hex. Proves transfer integrity, not authenticity — see P2 Q5. */
  sha256: string;
  capturedAt: string | null;
  capturedByName: string | null;
  appointmentId: string | null;
  createdAt: string;
  /** Short-lived and signed. The bucket itself is not publicly readable. */
  downloadUrl: string;
  /** Null for a PDF, which canvas cannot render — the screen shows a file icon. */
  thumbnailUrl: string | null;
}

/** What the API hands back so the browser can upload directly to storage. */
export interface PresignedUpload {
  uploadUrl: string;
  thumbnailUploadUrl: string | null;
  s3Key: string;
  thumbnailS3Key: string | null;
  expiresInSeconds: number;
}

export interface PresignUploadRequest {
  mimeType: string;
  sizeBytes: number;
  withThumbnail?: boolean;
}

export interface ConfirmUploadRequest {
  s3Key: string;
  thumbnailS3Key?: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  kind: AttachmentKindName;
  region?: string;
  appointmentId?: string;
}

// ── Billing (STORY-027) ─────────────────────────────────────────────────────
//
// **Records, not payments** (working plan §3.1). Every figure is an integer in
// minor units and the exponent follows `clinics.currency` — nothing here or
// downstream may assume two decimal places.

export const INVOICE_STATUSES = ["DRAFT", "ISSUED", "PARTIALLY_PAID", "PAID", "VOID"] as const;
export type InvoiceStatusName = (typeof INVOICE_STATUSES)[number];

export const PAYMENT_METHODS = ["CASH", "CARD", "BANK_TRANSFER", "CHEQUE", "OTHER"] as const;
export type PaymentMethodName = (typeof PAYMENT_METHODS)[number];

/** How each reads on screen. Sentence case, like every other micro-label. */
export const INVOICE_STATUS_LABELS: Record<InvoiceStatusName, string> = {
  DRAFT: "Draft",
  ISSUED: "Outstanding",
  PARTIALLY_PAID: "Part paid",
  PAID: "Paid",
  VOID: "Void",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodName, string> = {
  CASH: "Cash",
  CARD: "Card",
  BANK_TRANSFER: "Bank transfer",
  CHEQUE: "Cheque",
  OTHER: "Other",
};

export interface InvoiceLine {
  id: string;
  description: string;
  procedureCodeId: string | null;
  fdiNumber: number | null;
  quantity: number;
  unitPriceCents: number;
  /** **Always the amount actually charged**, so no report has to pick a column. */
  amountCents: number;
  /** What the branch's catalogue said when the line was raised. */
  cataloguePriceCents: number | null;
  priceOverridden: boolean;
  overrideNote: string | null;
  overriddenByName: string | null;
  overriddenAt: string | null;
}

export interface Payment {
  id: string;
  amountCents: number;
  method: PaymentMethodName;
  receivedAt: string;
  receivedByName: string | null;
  reference: string | null;
}

export interface Invoice {
  id: string;
  number: string;
  branchId: string;
  branchName: string;
  status: InvoiceStatusName;
  issuedAt: string | null;
  dueAt: string | null;
  subtotalCents: number;
  /** Always 0 — no tax engine, therefore no rounding rule (P2 Q7). */
  taxCents: number;
  totalCents: number;
  /** ISO 4217 as written (QA4-D6). Every amount on the invoice is in it. */
  currency: string;
  paidCents: number;
  outstandingCents: number;
  notes: string | null;
  appointmentId: string | null;
  createdAt: string;
  lines: InvoiceLine[];
  payments: Payment[];
}

/**
 * The four cards the mockup draws, plus the figure it has no room for.
 *
 * `unreconciledCents` is what an insurer settling **less than was claimed**
 * leaves behind. It moves nowhere on its own (P2 Q7, closing Q8) — an
 * underpayment is as often the insurer's error as a patient liability.
 */
export interface BillingSummary {
  totalBilledCents: number;
  /**
   * Everything received against live invoices — `totalBilledCents −
   * outstandingCents`. The Billing tab shows this one figure rather than the
   * insurer/patient split (client, 2026-08-28); the two below stay for anything
   * that still needs the breakdown.
   */
  totalPaidCents: number;
  insurerSettledCents: number;
  patientPaidCents: number;
  outstandingCents: number;
  unreconciledCents: number;
  /** ISO 4217 — every figure above is in it, and only it. */
  currency: string;
}

export interface Billing {
  /**
   * **One per currency, never one total across them** (QA4-D6) — a patient
   * seen at locations in two currencies owes two amounts. At least one: nothing
   * billed is a zero summary in the home currency.
   */
  summaries: BillingSummary[];
  invoices: Invoice[];
}

// ── Visits (STORY-028) ──────────────────────────────────────────────────────
//
// **A visit is a date, not an appointment.** Notes and attachments reach a
// branch only through their nullable `appointmentId`, so a note written at the
// desk has no appointment — and still belongs to the day it was written. The
// date resolves in the branch's timezone (P2 Q8), falling back to the patient's
// home branch and then the clinic default.

export interface VisitSummary {
  /** Local date in the resolving branch's timezone, `2026-01-05`. */
  date: string;
  /** Which timezone decided it. */
  timezone: string;
  branchName: string | null;
  appointmentId: string | null;
  appointmentStatus: string | null;
  title: string | null;
  providerName: string | null;
  /** ISO 4217, the visit's location's. Carried so the modal never assumes one. */
  currency: string;
  noteCount: number;
  imageCount: number;
  invoiceCount: number;
}

/** One date, tied together — and nothing from another date. */
/**
 * More work on a visit that is already completed, recorded and billed in one
 * action (EB7): procedure records on the chart and in History, and a new issued
 * invoice for them linked to the same visit.
 */
export interface AddVisitWork {
  appointmentId: string;
  /** A per-tooth procedure names its tooth; a whole-mouth one names none. */
  items: { procedureCodeId: string; fdiNumber?: number }[];
}

export interface VisitDetail extends VisitSummary {
  notes: ClinicalNote[];
  images: Attachment[];
  invoices: Invoice[];
}

// ── The calendar (Phase 3) ──────────────────────────────────────────────────

export const APPOINTMENT_STATUSES = [
  "NOT_CHECKED_IN",
  "CONFIRMED",
  "CHECKED_IN",
  "IN_CHAIR",
  "COMPLETED",
  "NO_SHOW",
  "CANCELLED",
] as const;
export type AppointmentStatusName = (typeof APPOINTMENT_STATUSES)[number];

/** How each status reads on screen. Sentence case, like every micro-label. */
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatusName, string> = {
  NOT_CHECKED_IN: "Not checked in",
  CONFIRMED: "Confirmed",
  CHECKED_IN: "Checked in",
  IN_CHAIR: "In chair",
  COMPLETED: "Completed",
  NO_SHOW: "No-show",
  CANCELLED: "Cancelled",
};

/**
 * **Where each status may go — the whole rule, defined once.**
 *
 * This lived in two hand-maintained copies until 2026-08-31: one in
 * `apps/api/src/appointments/status-graph.ts`, which decides, and one in
 * `apps/web/src/calendar/status-graph.ts`, which offers the buttons. The web
 * copy's own header said *"it must keep mirroring"* — by hand, which is a
 * promise rather than a mechanism, and exactly the shape that let the CSV price
 * importer disagree with the browser by a factor of a hundred (QA #69).
 *
 * An edge the browser has and the server does not is a button that always
 * fails; one the server has and the browser does not is a move nobody can make.
 * Neither is possible now.
 *
 * ── The shape (P3 Q3, P3 Q14, T3) ───────────────────────────────────────────
 *
 * ```
 * NOT_CHECKED_IN ⇄ CONFIRMED ⇄ CHECKED_IN → IN_CHAIR → COMPLETED
 * CANCELLED reachable from any state before COMPLETED
 * NO_SHOW reachable only before CHECKED_IN (FX27 — checked in means present)
 * NO_SHOW → CHECKED_IN (they turned up after all) or CANCELLED
 * nothing leaves COMPLETED or CANCELLED
 * ```
 *
 * **One step at a time, in either direction.** The backward edges are T3
 * (2026-08-31): a patient who confirms and then goes quiet is ordinary, and a
 * front desk changing statuses all day will check somebody in by accident.
 * Every transition writes an `appointment_status_events` row, so a reversal is
 * recorded rather than hidden — which is why P3 Q3's objection to it did not
 * survive being re-examined.
 *
 * An empty list is a dead end and says so by being empty; there is no separate
 * terminal flag to fall out of step with the edges.
 */
export const APPOINTMENT_STATUS_EDGES: Record<
  AppointmentStatusName,
  readonly AppointmentStatusName[]
> = {
  NOT_CHECKED_IN: ["CONFIRMED", "NO_SHOW", "CANCELLED"],
  CONFIRMED: ["CHECKED_IN", "NOT_CHECKED_IN", "NO_SHOW", "CANCELLED"],
  // FX27: a checked-in patient is in the building, so never a no-show.
  CHECKED_IN: ["IN_CHAIR", "CONFIRMED", "CANCELLED"],
  // FX29: once treatment starts it is finished or abandoned — no step back to
  // waiting (narrowing T3) and no no-show.
  IN_CHAIR: ["COMPLETED", "CANCELLED"],
  // P3 Q14: they turned up after all, or the desk gave up on them.
  NO_SHOW: ["CHECKED_IN", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: [],
};

/**
 * One booking in a chair.
 *
 * **`patient` is null for a buffer block** — a chair held for maintenance, a
 * meeting or a break. A block also carries `turnoverMinutes: 0`, which is what
 * lets another booking sit flush against it.
 */
export interface Appointment {
  id: string;
  branchId: string;
  chairId: string;
  providerId: string | null;
  startsAt: string;
  endsAt: string;
  /**
   * When the chair is free again — `endsAt` plus this booking's own turnover.
   *
   * **Sent so nothing has to compute it.** Dragging (STORY-034) needs to know
   * whether a proposed slot collides while the pointer is still moving, and a
   * browser adding turnover itself would be a second implementation of P3 Q1,
   * free to drift from the server's. Overlapping two intervals is arithmetic;
   * deciding how long a chair is held is a rule.
   */
  occupiedUntil: string;
  /** Copied from the branch at creation, never read live. 0 for a block. */
  turnoverMinutes: number;
  status: AppointmentStatusName;
  procedureSummary: string | null;
  /**
   * The catalogue label for [[procedureCodeId]], where there is one.
   *
   * **What a picked booking is called.** `procedureSummary` holds what somebody
   * typed, and picking from the catalogue leaves it empty — so a screen showing
   * only the summary has nothing to say about a booking made by picking, which
   * is every booking made from a treatment plan.
   */
  procedureLabel: string | null;
  /**
   * Its procedure's category colour (PG-D7), a palette key drawn on the block's
   * top and right borders. Null with no procedure, and on a buffer block.
   */
  procedureCategoryColour: ProcedureCategoryColour | null;
  /**
   * Its procedure's category (CF-D16's "Appointment type"), null with no
   * procedure. Optional only because older fixtures predate it; the server
   * always sends it.
   */
  procedureCategoryId?: string | null;
  /**
   * **The patient's first visit** (CF-D4's "New" tag): no earlier booking of
   * theirs that was kept or is still to come — cancelled and missed ones do not
   * count. False for a held chair. Optional for the same reason as above.
   */
  isFirstVisit?: boolean;
  /**
   * Which catalogue procedure this is (decision TP18). **Null is normal** — a
   * booking typed rather than picked has none, and so does every appointment
   * made before STORY-110a. No procedure means no prep list, and nothing is
   * refused for it.
   */
  procedureCodeId: string | null;
  /** The teeth this booking is for (BF3, BF6), empty for none. Red on the chart while live. */
  fdiNumbers: number[];
  awaitingValidation: boolean;
  patient: {
    id: string;
    fileNumber: number;
    fileNumberDisplay: string;
    firstName: string;
    lastName: string;
    phoneE164: string;
    flags: PatientFlagSummary[];
    hasCriticalFlag: boolean;
  } | null;
  /**
   * Set when this is a plan visit with later visits booked (CH4) — which plan,
   * and how many. Lets a move ask "move the rest too?" from the day already on
   * screen. Null otherwise; absent where the server does not compute it.
   */
  chain?: { planId: string; laterBooked: number } | null;
  /**
   * **The family visit this booking is part of** (FV-D1, STORY-197): the
   * bookings sharing it are one visit, one per patient, back to back in one
   * chair. Null for an ordinary booking; absent from older fixtures.
   */
  visitGroupId?: string | null;
  /** The booking's procedures after `procedureCodeId`, in order (FV-D6). */
  extraProcedures?: { procedureCodeId: string; label: string }[];
  /**
   * **The rest of the family, worked out in the browser** (`groupVisits`): set
   * on the first booking of a visit, which then stands for the whole of it on
   * the grid — its end is the last one's. Never sent by the server.
   */
  party?: Appointment[];
}

/**
 * Moving a booking — to another time, another chair, or both.
 *
 * **No duration.** A move changes when and where a booking happens, never how
 * long; the end is recomputed server-side from the existing length, so a move
 * cannot quietly resize.
 */
export interface MoveAppointment {
  startsAt: string;
  chairId?: string;
}

/**
 * Editing a booking (QA4-D3): what it is for, its teeth, who treats, and its
 * description. Not the patient, not the slot. Each field left out is left
 * alone; `null` clears.
 */
export interface EditAppointment {
  procedureCodeId?: string | null;
  fdiNumbers?: number[];
  providerId?: string | null;
  procedureSummary?: string | null;
}

/** What it takes to book. Omit `patientId` and it is a buffer block. */
export interface CreateAppointment {
  chairId: string;
  startsAt: string;
  durationMinutes: number;
  patientId?: string;
  providerId?: string;
  procedureSummary?: string;
  /**
   * A procedure from this branch's catalogue (TP18). **Optional and staying
   * that way**: picking one gives the booking a prep list, typing something the
   * catalogue lacks still books and links nothing, and no booking is ever
   * refused for having none.
   */
  procedureCodeId?: string;
  /** The teeth, FDI (BF3, BF6). Refused on a held chair and a whole-mouth procedure. */
  fdiNumbers?: number[];
  /**
   * Why the chair is held — **for a buffer block, and it is what reaches
   * Google** (STORY-046d, decision G12).
   *
   * A row from this branch's block reasons, never free text. `notes` and
   * `procedureSummary` are both free text used for clinical detail, and sending
   * either verbatim is what G12 exists to forbid. Ignored on a booking that has
   * a patient — a reason describes a chair nobody is in.
   */
  blockReasonId?: string;
  notes?: string;
  turnoverMinutes?: number;
  /** More procedures, after `procedureCodeId`, in order (FV-D6). */
  moreProcedureCodeIds?: string[];
  /**
   * **The rest of a family visit** (FV-D1–FV-D3), booked after this patient in
   * this order, in the same chair with the same doctor — each their own length
   * and at least one procedure.
   */
  party?: { patientId: string; durationMinutes: number; procedureCodeIds: string[] }[];
}

/**
 * The times a booking could move to — what `GET /appointments/:id/slots`
 * returns and what the reschedule picker draws.
 *
 * **Instants, not clock readings.** `startsAt` carries ISO timestamps, so the
 * browser renders them in `timezone` rather than reassembling a date from
 * `"09:15"` and its own offset — which is wrong for anyone reading a branch in
 * a zone other than their own.
 *
 * Every entry would be accepted by a move made immediately; none is a promise
 * about a move made later, which is why choosing one still goes through
 * `PATCH :id/move` and can still be refused (§6.1).
 */
export interface AppointmentSlots {
  /** The booking's own day. The picker does not cross days (§6.1, P3 Q12). */
  localDate: string;
  timezone: string;
  /** Preserved by a move — the slot fits the booking, not the other way round. */
  durationMinutes: number;
  /** Ascending. Empty is a real answer: a full chair, or a day the branch is shut. */
  startsAt: string[];
}

/** The four states a request can be in. Mirrors `booking_request_state`. */
export type BookingRequestState = "PENDING" | "APPROVED" | "DECLINED" | "EXPIRED";

/** The booking a reschedule request would move, as the queue shows it. */
export interface RescheduledAppointment {
  id: string;
  chairId: string;
  /** Where it is now — what the request wants changed. */
  startsAt: string;
  endsAt: string;
  status: string;
  /** Null for a buffer block. */
  patientName: string | null;
  /**
   * Whether it could move at all right now — false for a completed or
   * cancelled booking (§6.1). **Computed server-side**, so the screen can
   * disable a button without applying a scheduling rule of its own.
   */
  isMovable: boolean;
}

/**
 * One request in the approval queue.
 *
 * **`reschedules` is what tells the two kinds apart** (P3 Q5). Null is a request
 * for a *new* booking — ADR-0005's shape, where the patient's confirmation has
 * already put an appointment on the calendar and staff validate afterwards. Set
 * is a request to *move* one, which staff approve before it happens, because the
 * patient may have arranged their day around the booking that already exists.
 */
/**
 * What the header chip reads (STORY-057, decision HC1).
 *
 * ADR-0005 split "waiting on WhatsApp" into two queues with two different acts
 * — validating a booking that already holds a chair, and approving a proposal
 * that holds nothing. **`total` is what the chip shows**; the halves are what
 * the list behind it uses for its two sections, and they arrive together so the
 * number and the headings can never come from two reads a second apart.
 */
export interface WaitingCount {
  awaitingValidation: number;
  requests: number;
  total: number;
}

export interface BookingRequest {
  id: string;
  branchId: string;
  state: BookingRequestState;
  source: string;
  patientId: string | null;
  patientName: string | null;
  phoneE164: string;
  requestedProcedure: string | null;
  urgency: string | null;
  /** The time being asked for. */
  proposedStartsAt: string | null;
  /** Ignored when approving a reschedule — a move preserves the length (§6.1). */
  proposedEndsAt: string | null;
  chairId: string | null;
  reschedulesAppointmentId: string | null;
  reschedules: RescheduledAppointment | null;
  decidedById: string | null;
  decidedAt: string | null;
  resultingAppointmentId: string | null;
  createdAt: string;
}

/** A status move. The destination only — the origin is whatever the row says. */
export interface ChangeAppointmentStatus {
  status: string;
  /** Why, in a few words. Stored on the audit event (ADR-0005). */
  note?: string;
  /**
   * Procedures done at this visit that belong to no single tooth — an
   * examination, a scale and polish.
   *
   * Only read when `status` is `COMPLETED`. Per-tooth work is already linked
   * to the appointment by charting; whole-mouth work has nowhere to be charted,
   * because `tooth_procedures.fdi_number` is not nullable, so it is named here
   * instead and billed from the branch catalogue.
   */
  wholeMouthCodeIds?: string[];
  /**
   * **A family visit's whole-mouth work, per booking** (FV-D5): completing it
   * completes every child, each billed to their own file.
   */
  wholeMouthByAppointment?: { appointmentId: string; codeIds: string[] }[];
}

/** One recorded transition, as the panel reads it back. */
export interface AppointmentStatusEvent {
  id: string;
  /** Null for the first recorded state. */
  fromStatus: string | null;
  toStatus: string;
  changedAt: string;
  /** Null when the actor has since been removed. */
  changedByName: string | null;
  note: string | null;
}
/** Mirrors `waitlist_status`. */
export type WaitlistStatus = "WAITING" | "OFFERED" | "BOOKED" | "EXPIRED" | "CANCELLED";

/**
 * One patient waiting for an earlier slot.
 *
 * **Its own thing, not an appointment status** (working plan §3.7). The queue is
 * people; a freed slot is a gap in the grid.
 */
export interface WaitlistEntry {
  id: string;
  branchId: string;
  patientId: string;
  patientName: string;
  patientFileNumber: string;
  patientPhone: string;
  procedureCodeId: string | null;
  procedureLabel: string | null;
  /**
   * **How long this patient needs the chair for** — the length of the
   * appointment they are waiting for, not how long they have been waiting.
   *
   * The distinction is not obvious from a field called "minutes", which is what
   * the screen used to label it: *"the minutes what do they really mean (manna
   * mafhoume eno ade bedo waet, maybe needed time?)"* — Anthony's QA, item 13.
   * He guessed right, and the label now says it.
   */
  durationMinutes: number;
  /**
   * Free text — "mornings", "any Tuesday", "after 4pm". Deliberately not an
   * enum, so it is shown to whoever is deciding and never matched on. The three
   * fields below carry the machine-readable half; this stays the note.
   */
  preferredWindow: string | null;
  /** ISO weekdays — Monday 1, Sunday 7. **Empty means any day.** */
  preferredWeekdays: number[];
  preferredPartOfDay: WaitlistPartOfDay;
  /** Not before this date. ISO `yyyy-mm-dd`, or null for no lower bound. */
  waitingFrom: string | null;
  /** Not after this date. ISO `yyyy-mm-dd`, or null for no upper bound. */
  waitingUntil: string | null;
  flexibilityNote: string | null;
  status: WaitlistStatus;
  createdAt: string;
}

/**
 * Which half of the day a waiting patient can come in (Anthony's QA, item 13).
 *
 * **Coarse on purpose** — the point is to shrink a queue of forty to the six
 * people who could take a slot at 09:30, not to describe availability
 * precisely. The free-text note beside it does that, and always will do it
 * better. `ANY` is the default and matches every filter.
 */
export const WAITLIST_PARTS_OF_DAY = ["ANY", "MORNING", "AFTERNOON", "EVENING"] as const;
export type WaitlistPartOfDay = (typeof WAITLIST_PARTS_OF_DAY)[number];

export const WAITLIST_PART_OF_DAY_LABELS: Record<WaitlistPartOfDay, string> = {
  ANY: "Any time",
  MORNING: "Mornings",
  AFTERNOON: "Afternoons",
  EVENING: "Evenings",
};

/** Monday first, as `branch_hours` numbers them and as a rota is read. */
export const WEEKDAY_LABELS: readonly { value: number; short: string; long: string }[] = [
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
  { value: 7, short: "Sun", long: "Sunday" },
];

export interface AddWaitlistEntry {
  patientId: string;
  durationMinutes: number;
  procedureCodeId?: string;
  preferredWindow?: string;
  flexibilityNote?: string;
  preferredWeekdays?: number[];
  preferredPartOfDay?: WaitlistPartOfDay;
  waitingFrom?: string;
  waitingUntil?: string;
}

/**
 * Any subset of a waiting patient's details, **except who they are**.
 *
 * An entry is *this* person waiting; pointing it at somebody else would be a
 * new entry wearing an old one's seniority, so `patientId` is not editable.
 * Editing keeps `createdAt`, which is the whole point — the queue is ordered
 * oldest-first and correcting a typo must not cost somebody their turn.
 */
export type UpdateWaitlistEntry = Omit<AddWaitlistEntry, "patientId">;

/** Narrowing the queue to who could take a given slot (Anthony's QA, item 13). */
export interface WaitlistFilters {
  status?: WaitlistStatus;
  /** ISO weekday — Monday 1, Sunday 7. */
  weekday?: number;
  partOfDay?: WaitlistPartOfDay;
  /** Only people whose window reaches this date or later. */
  from?: string;
  /** Only people whose window starts by this date. */
  to?: string;
}

/** A freed slot, and who could take it. */
export interface WaitlistMatches {
  chairId: string;
  startsAt: string;
  minutes: number;
  /** Everyone waiting who actually fits, oldest first. */
  entries: WaitlistEntry[];
}

/** Taking a waiting patient into the freed slot. The length comes from the entry. */
export interface BookFromWaitlist {
  chairId: string;
  startsAt: string;
  providerId?: string;
  procedureSummary?: string;
}

/** One occurrence of a series that could not be placed, and why. */
export interface RefusedOccurrence {
  startsAt: string;
  reason: string;
  /** The sentence a receptionist acts on. */
  message: string;
}

/**
 * What a series operation produced.
 *
 * **Both lists have to be read** (P3 Q15). A success means *some* occurrences
 * were placed, never all of them — treating it as "booked" is how a patient
 * ends up believing they have twelve appointments and having ten.
 */
export interface SeriesResult {
  recurrenceRuleId: string;
  booked: Appointment[];
  /** Empty when every occurrence fitted. Never assume it is. */
  refused: RefusedOccurrence[];
}

/** Book a repeating course of treatment. */
export interface CreateSeries {
  /**
   * RFC 5545, and **a deliberate subset**: `FREQ` (WEEKLY or DAILY), `INTERVAL`
   * and `COUNT`. Anything else is refused rather than skipped.
   */
  rrule: string;
  chairId: string;
  startsAt: string;
  durationMinutes: number;
  patientId?: string;
  providerId?: string;
  procedureSummary?: string;
  /** Carried onto every occurrence, so a recurring recall keeps its prep list. */
  procedureCodeId?: string;
}

/**
 * One provider, and whether they are working in this branch on a given day.
 *
 * **A filter, never a refusal** (P3 Q16). The picker offers who is rostered;
 * booking somebody else still succeeds, because a rota is softer information
 * than a chair being physically occupied — an emergency must never be blocked by
 * a rota nobody has updated yet.
 */
export interface ProviderAvailability {
  userId: string;
  name: string;
  role: string;
  /** `CLINIC`, `HALF_DAY`, `TRAINING` or `OFF`. */
  kind: string;
  /** True for `CLINIC` and `HALF_DAY` only — training is not seeing patients. */
  isAvailable: boolean;
  /** `09:00`, or null when the row states no hours. */
  startsAt: string | null;
  endsAt: string | null;
  /** True when a one-day exception decided this rather than the weekly pattern. */
  fromException: boolean;
  /**
   * **Why they are not available, when they really are not** (FX19):
   * `LEAVE` for approved time off, `TRAINING` for a course, `OFF` for a day
   * their rota or an exception says they do not work. **Null when available,
   * and null for somebody with no rota at this location at all** — a clinic
   * that has not built schedules yet must not see every dentist labelled as
   * away.
   */
  away: "LEAVE" | "TRAINING" | "OFF" | null;
}

/** One day in the week view — the clustered load, and who is away. */
export interface WeekDay {
  /** Local date in the branch's own timezone. */
  date: string;
  /** `Mon`. Rendered by the server so seven cells cannot disagree about it. */
  weekday: string;
  /** True on the **branch's** today, not the reader's. */
  isToday: boolean;
  appointmentCount: number;
  clusters: {
    startsAt: string;
    endsAt: string;
    count: number;
    /** `Chairs 1–3`, already phrased — the browser never assumes a chair is numbered. */
    chairs: string;
  }[];
  /** Rostered staff who are not working. The mockup's dashed away strips. */
  away: { userId: string; name: string; kind: string }[];
}

/**
 * Seven days at a glance (P3 Q7).
 *
 * Grouped blocks with a count and a chair range, not three chairs of
 * fifteen-minute blocks across seven days — a week is for planning rather than
 * booking, and the dense version is unreadable at that width.
 */
export interface Week {
  from: string;
  to: string;
  timezone: string;
  /**
   * Earliest opening and latest closing **across all seven days**, as minutes
   * since midnight. Null on both means the branch is shut all week, which the
   * screen says rather than inventing an 08:00–18:00 day.
   */
  /** Never null since H3 — the grid's floor applies even to a week with no hours. */
  opensAt: number;
  closesAt: number;
  totalAppointments: number;
  days: WeekDay[];
}

/** One cell in the month grid. */
export interface MonthDay {
  /** Local date in the branch's own timezone. */
  date: string;
  dayOfMonth: number;
  /** True on the **branch's** today, not the reader's. */
  isToday: boolean;
  /** False when the branch has no hours that weekday — the mockup's "closed". */
  isOpen: boolean;
  appointmentCount: number;
}

/**
 * Load per day across a month — the planning view.
 *
 * **Counts, not clusters.** A month cell shows a number, so grouping a day into
 * bands would be work nobody sees.
 */
export interface Month {
  /** `2026-03`. */
  month: string;
  timezone: string;
  /**
   * How many blank cells lead the grid, so the first of the month lands under
   * its own weekday. **Sunday-first**, matching the mockup.
   */
  leadingBlanks: number;
  totalAppointments: number;
  days: MonthDay[];
}

/** Why a slot was refused, beside the sentence a human reads. */
export type ConflictReason =
  | "CHAIR_BUSY"
  | "ENDS_BEFORE_IT_STARTS"
  | "PATIENT_BUSY"
  // CF-D10: the doctor is booked at another location at that time.
  | "PROVIDER_ELSEWHERE";

/**
 * One clinician rostered to one chair on one day.
 *
 * **A roster fact, not a property of the chair.** `chairs` carries no provider,
 * because who is in a room is a fact about the day rather than about the room —
 * and a clinician is never bound to a chair (settled decision 4.3).
 */
export interface ChairAssignment {
  id: string;
  chairId: string;
  chairName: string;
  userId: string;
  providerName: string;
  /** Local date in the branch's timezone. */
  date: string;
  /** Null means the whole day. A chair may change hands at lunchtime. */
  startsAt: string | null;
  endsAt: string | null;
}

/** One chair and everyone on it that day — the calendar's column header. */
export interface ChairRoster {
  chairId: string;
  chairName: string;
  assignments: ChairAssignment[];
  /** `Chair 1 · Dana Haddad`, derived. Never stored. */
  header: string;
}

/**
 * When the branch is open on a day, and the clock the day is read on.
 *
 * **Not what the grid draws.** P3 Q11: the axis spans the union of these bounds
 * and the day's appointments, so a booking that outlived a change to the hours
 * stays visible at its true minute. `isClosed` with appointments present is a
 * real state — the branch stopped opening Tuesdays after somebody was booked in.
 */
export interface DayFrame {
  /** The branch's own zone. The now-line is the branch's now, not the reader's. */
  timezone: string;
  /** `10:00`, earliest opening that weekday. Null when the branch is shut. */
  opensAt: string | null;
  /** `16:00`, latest closing. Null when the branch is shut. */
  closesAt: string | null;
  isClosed: boolean;
  /**
   * What the grid draws (STORY-177): minutes since midnight, from the branch's
   * own settings. **Not opening hours** — display only. The grid stretches past
   * these for a day with a booking outside them.
   */
  calendarOpensAt: number;
  calendarClosesAt: number;
  /** The scale as a percent of the original 1.5 px per minute. */
  calendarZoom: number;
}

/**
 * An event on a chair's Google calendar that DentaCore did not create
 * (STORY-049b, decision G14).
 *
 * **Deliberately not an `Appointment`, and never convertible into one.** It has
 * no patient, no procedure, no status and no invoice, it never enters
 * appointment counts, and Google stays its source of truth — editing it there
 * moves it here. Folding these into the appointment list would have been less
 * code and is exactly what G14 rejected: *"appointments today"* would silently
 * start counting renovations and lunch breaks.
 *
 * A separate type is what makes that impossible to get wrong by accident, and
 * it is why the grid asks for these on their own endpoint.
 */
export interface ExternalBlock {
  id: string;
  chairId: string;
  /**
   * The span the block actually holds, **already expanded to the branch's own
   * clock** (G15).
   *
   * An all-day event is stored as midnight UTC of Google's date strings; the
   * server expands it to branch-local midnight before sending, so nothing in
   * the browser has to know the difference — and nothing can implement the
   * expansion a second time and disagree with the conflict service about which
   * minutes are closed.
   */
  startsAt: string;
  endsAt: string;
  /** What it is called in Google. Null when whoever made it left it untitled. */
  summary: string | null;
  /** Google said `date` rather than `dateTime` — the whole day is closed. */
  isAllDay: boolean;
}

export interface DayRoster {
  date: string;
  frame: DayFrame;
  chairs: ChairRoster[];
}

export interface AssignChair {
  chairId: string;
  userId: string;
  date: string;
  startsAt?: string;
  endsAt?: string;
}

/** One branch a staff member works at. */
export interface StaffMembership {
  branchId: string;
  branchName?: string;
  isPrimary?: boolean;
  /**
   * Appointments at this branch, starting after now, where they are the named
   * provider. **Revoking is warned on this and then allowed** (decision A4) —
   * unlike retiring a chair, which is refused, because these appointments keep
   * their provider and stay exactly where they are.
   */
  futureAppointmentCount?: number;
}

/** A staff member, with the branches they work at. What `GET /users` returns. */
export interface StaffMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** What they are clinically. */
  role: string;
  /** Whether they may reach the admin-only screens. Independent of role. */
  canAdminister: boolean;
  phoneE164: string | null;
  /**
   * The number a WhatsApp assistant will reach them on (Phase 11), if an
   * admin has set one — **set by an admin, not by this person** (STORY-120
   * grill, 2026-09-19). Distinct from `phoneE164` above, which is ordinary
   * contact detail.
   */
  assistantPhoneE164: string | null;
  /**
   * False means either **a leaver** or **someone who has not accepted their
   * invite yet** (STORY-064) — login refuses both, and the two are told apart
   * by whether an invite is outstanding rather than by this flag.
   */
  isActive: boolean;
  memberships: StaffMembership[];
  /**
   * The clinician's own Google address, or null (decision G22).
   *
   * Setting it is what makes DentaCore create a calendar of their work and
   * share it with them; clearing it takes the calendar away.
   */
  googleCalendarEmail: string | null;
  /**
   * STORY-122 (SA3) — set only by `create`/`update`, never by `GET /users`.
   * Non-null when the assistant number just set collides with a patient's
   * own number or a guardian contact's. A signal, not a guard.
   */
  patientCollision?: { patientId: string; patientName: string; fileNumberDisplay: string } | null;
}

/** What an admin may change about someone else. Never the email or password. */
export interface UpdateStaffMember {
  role?: string;
  /** Whether they may reach the admin-only screens. Independent of role. */
  canAdminister?: boolean;
  isActive?: boolean;
  /** YYYY-MM-DD, saved with a deactivation so their salary stops after it (QF-D7). */
  lastDay?: string;
  phoneE164?: string;
  /**
   * The number a WhatsApp assistant will reach them on (Phase 11). `null` or
   * empty removes it, revoking assistant access immediately (SA4) — omitted
   * leaves it alone, the same three-way distinction `googleCalendarEmail`
   * below needs and for the same reason.
   */
  assistantPhoneE164?: string | null;
  /**
   * Their own Google address (decision G22). `null` removes the calendar.
   *
   * Omitted leaves it alone — the three-way distinction matters, because a form
   * that sent `null` for "unchanged" would delete a calendar every time
   * somebody edited a phone number.
   */
  googleCalendarEmail?: string | null;
}

/** Adding a staff member. Omit the password and they are invited instead. */
export interface CreateStaffMember {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phoneE164?: string;
  /** The number a WhatsApp assistant will reach them on (Phase 11). */
  assistantPhoneE164?: string;
  password?: string;
}

/** `POST /users` — the invite is present only when no password was supplied. */
export interface CreatedStaffMember extends StaffMember {
  invite?: IssuedInvite;
}

/**
 * The day grid's minimum span, in minutes since local midnight — **08:00 to
 * 18:00** (decision T5, 2026-08-31, widening H3's 09:00–17:00, which had
 * replaced the 07:00–20:00 floor of 2026-08-23).
 *
 * The one place an opening-hour literal is allowed to exist. It is a
 * **rendering** floor, not a clinic's hours: it only ever *widens* what would
 * otherwise be drawn, so real hours and real bookings reaching outside it still
 * stretch the axis and nothing is ever hidden. It applies unconditionally,
 * which is what stops a branch that has never had hours set from drawing no
 * calendar at all.
 *
 * **Why it moved a third time.** 09:00–17:00 was chosen so a light day did not
 * read as a sliver of screen, and it turned out to hide two things instead: an
 * early or late booking sat outside the drawn day with no way to scroll to it,
 * and the now-line had nowhere to render after five o'clock — which is exactly
 * when somebody checks how the day is running. The client reported the now-line
 * as missing; it had been built since STORY-032.
 *
 * Shared rather than duplicated because both sides read it: the web draws the
 * axis with it and the API offers reschedule slots against it, and two copies
 * would drift the first time either moved.
 */
export const GRID_FLOOR_OPENS_AT = 8 * 60;
export const GRID_FLOOR_CLOSES_AT = 18 * 60;

/**
 * One weekday of a branch's opening hours.
 *
 * **Display only** (H2, 2026-08-25): these set where the day grid's axis opens
 * and closes and refuse no booking. `opensAt`/`closesAt` are `HH:MM` on a
 * 24-hour clock rather than instants — a branch's opening time is a wall-clock
 * fact with no date and no zone, and the branch's own timezone decides what
 * 09:00 means.
 *
 * All seven weekdays always come back. A day with no times set is not the same
 * fact as a day the branch has marked shut, and collapsing them would make
 * "never configured" unreadable.
 */
export interface BranchHour {
  /** ISO-8601: 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  opensAt: string | null;
  closesAt: string | null;
  isClosed: boolean;
}

/**
 * The whole week, replaced in one write — a weekday is one opens/closes pair
 * (H1), so there is nothing to add or remove individually.
 */
export interface SetBranchHours {
  days: BranchHour[];
}

/**
 * A single-use invite link, returned **once** to the admin who issued it
 * (STORY-064). The server keeps only a hash, so it can never be read back —
 * losing it means reissuing, which invalidates whatever came before.
 */
export interface IssuedInvite {
  /**
   * Null for someone without `access.assign`, as is `acceptUrl`: their invites
   * go by email only, so nobody can sign in as a colleague who has not joined
   * yet (QF-D24).
   */
  token: string | null;
  /** ISO-8601. Seven days out; reissue rather than extend. */
  expiresAt: string;
  /**
   * The whole link, at **the clinic's own subdomain** — not a path for a screen
   * to bolt onto wherever it happens to be running (QA20-5, 2026-09-20).
   *
   * Four screens used to build this as `` `${window.location.origin}/accept/…` ``
   * and it was wrong from every one of them sooner or later. An operator is
   * always at the apex, so the console's links were *always* wrong; an admin may
   * run their clinic from the apex too (D3), so a practice's own screens were
   * wrong whenever they did. A link at the apex reaches an accept page that
   * cannot resolve a tenant, the token is refused, and the visitor was then
   * redirected into the inviter's dashboard (QA20-6).
   *
   * It comes from `inviteUrl()` — the same call that builds the emailed link, so
   * the link on screen and the link in the inbox cannot disagree.
   */
  acceptUrl: string | null;
  /**
   * Whether the invite reached an inbox (QA20-4, reversing I1's "no email").
   *
   * **False is not a failure.** The link is in this response either way — that
   * is the half of I1 worth keeping — so this only decides whether the screen
   * says "we've emailed them" or "send this to them yourself".
   */
  emailed: boolean;
}

/**
 * What a practice tells us to bring itself into existence (2026-09-05).
 *
 * Not one field is a clinical or operational value — branches, chairs, hours,
 * staff and prices are entered afterwards, on the screens that own them. A
 * sign-up form asking for opening hours would be inventing a default the moment
 * somebody left it alone.
 */
export interface SignupInput {
  clinicName: string;
  slug: string;
  timezone: string;
  currency: string;
  defaultCountry?: string;
  locale?: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  /**
   * Optional, and not a sign-in method. It becomes
   * `clinics.google_workspace_email` — the account Phase 4 grants ownership of
   * each chair's calendar. Nothing happens to it at sign-up.
   */
  googleAccountEmail?: string;
  /** QA4-D5: the working day, `HH:MM`, and the ISO weekdays worked. */
  defaultShiftStartsAt?: string;
  defaultShiftEndsAt?: string;
  defaultWorkingDays?: number[];
}

/** Where the new practice lives, and the token that gets the browser there. */
export interface SignupResult {
  clinicName: string;
  slug: string;
  origin: string;
  /** Five minutes, single use, and not a session on its own. */
  handoff: string;
  emailed: boolean;
}

/** Whether a subdomain can be taken, asked while somebody is still typing. */
export interface SlugAvailability {
  slug: string;
  available: boolean;
  /** Why not, in a sentence the form can show. Null when it is free. */
  reason: string | null;
}

/** Who an invite link is for, so the accept page can greet them. */
export interface InviteSubject {
  email: string;
  firstName: string;
  lastName: string;
  clinicName: string;
  /**
   * Whether the accept screen has to ask for a password (decision I2).
   *
   * **False when this person already works somewhere on the platform.** They
   * keep the password they have — a doctor at two practices has one account —
   * so the screen asks them to confirm they want to join rather than to choose
   * a second password they would then have to remember which of.
   */
  needsPassword: boolean;
}

/** Adding a procedure to a branch's own catalogue (ADR-0008). */
export interface CreateProcedureCode {
  code: string;
  label: string;
  category?: string;
  /** Required (PG-D4) — one of this clinic's categories. */
  procedureCategoryId: string;
  defaultDurationMinutes: number;
  /**
   * **Integer minor units, always.** The exponent follows `clinics.currency`, so
   * nothing that formats this may assume two decimal places. A plan item or an
   * invoice line copies it at creation and keeps its own, so editing it here
   * never rewrites anything already recorded.
   */
  defaultPriceCents: number;
  /** Omit for `PER_TOOTH` — see [[ProcedureScope]]. */
  scope?: ProcedureScope;
  /** Done where a tooth is missing (PP-D4). Defaults to false. */
  doneOnMissingTooth?: boolean;
}

/** `isActive: false` retires it. Codes are never deleted (decision C3). */
export interface UpdateProcedureCode extends Partial<CreateProcedureCode> {
  isActive?: boolean;
}

/** One row an import could not use, named by the line number the admin sees. */
export interface SkippedRow {
  line: number;
  code: string;
  reason: string;
}

/** Every row accounted for: a bad one never stops the good ones. */
export interface ImportResult {
  added: number;
  updated: number;
  skipped: SkippedRow[];
}

// ── Guided setup (STORY-069) ────────────────────────────────────────────────

/**
 * The five steps `/setup` renders. **Four are required and `hours` is not**
 * (decision G1): H2 made opening hours display only, so a branch that has never
 * had them is already bookable, and a checklist demanding them would block a
 * clinic on a step the product does not have.
 */
export const SETUP_STEP_KEYS = ["locations", "chairs", "providers", "procedures", "hours"] as const;

export type SetupStepKey = (typeof SETUP_STEP_KEYS)[number];

export interface SetupStep {
  key: SetupStepKey;
  done: boolean;
  /** `false` for `hours`, and only for `hours` — G1. */
  required: boolean;
  /** How many exist. Locations for `hours`, rows for the rest. */
  count: number;
  /**
   * Which locations lack it, by name — the acceptance check's *reports honestly
   * what is still missing*. A count tells a four-location clinic that something
   * is wrong; a name tells it where to go. Null when nothing is missing.
   */
  missing: string | null;
}

/** One location's own readiness. Chairs and the catalogue are per branch (ADR-0008). */
export interface SetupLocation {
  branchId: string;
  name: string;
  chairCount: number;
  procedureCount: number;
  /** Reported, never required — H2. */
  hasHours: boolean;
  /** A chair and a procedure. Hours are not part of it. */
  ready: boolean;
}

/**
 * Counted at read time, every field of it — no completion flag, no dismissal
 * and no column (decision G4). So it cannot disagree with the clinic's actual
 * data, and the page stays useful to a clinic opening its second location.
 */
export interface SetupState {
  /**
   * One location with both a chair and a procedure, plus a provider anywhere in
   * the clinic. Deliberately **not** "a chair exists and a procedure exists": a
   * clinic whose first location has the chairs and whose second has the
   * catalogue satisfies that while having nowhere that can actually be booked.
   */
  ready: boolean;
  readyLocationCount: number;
  requiredStepsDone: number;
  requiredStepsTotal: number;
  steps: SetupStep[];
  locations: SetupLocation[];
}

// ── The clinic's own details (STORY-070) ────────────────────────────────────

/**
 * The clinic as its settings screen reads it.
 *
 * `slug` and `locale` are returned and **not writable**: the slug is the
 * customer's subdomain, so changing it breaks every bookmark the practice
 * holds, and the locale is a provisioning input no screen asks for.
 *
 * **`defaultCountry` used to be in that list and no longer is** (QA #5, #35).
 * Calling it a provisioning input was the mistake: two live code paths read it
 * — phone normalisation and phone search — so leaving it unwritable meant every
 * clinic ran with it null, national numbers were unenterable, and the refusal
 * message told admins to set a field that had no setter.
 */
export interface Clinic {
  id: string;
  name: string;
  slug: string;
  /**
   * IANA zone. **Seeds new branches only** — `branches.timezone` is required per
   * branch rather than inherited, so changing this moves nothing already
   * created, and the screen says so rather than implying a clinic just moved.
   */
  defaultTimezone: string;
  /**
   * ISO 4217, and **effectively immutable once a price exists** (decision L2).
   * It converts nothing: every price is integer minor units and this supplies
   * the exponent, so 1000 stored units reads as `10.00` at two decimals and
   * `1.000` at KWD's three.
   */
  currency: string;
  locale: string;
  defaultCountry: string | null;
  /**
   * The practice's own email. **Phone and address are not here** (EB4): each
   * location carries its own, and a one-location practice typed them twice.
   */
  email: string | null;
  /**
   * The default working day, `HH:MM` (ST3–ST5): what a never-set staff week
   * opens pre-filled with, Monday to Friday. Starts at 09:00–17:00.
   */
  defaultShiftStartsAt: string;
  defaultShiftEndsAt: string;
  /** ISO weekdays the practice works, 1 = Monday … 7 = Sunday (QA4-D5). */
  defaultWorkingDays: number[];
  /** Whether one is set at all — true even after `logoUrl` has expired. */
  hasLogo: boolean;
  /**
   * A short-lived signed URL, minted per read. **Never a stable address**: the
   * bucket is private and the expiry is the access control, exactly as it is
   * for a radiograph (STORY-025).
   */
  logoUrl: string | null;
  /** Salaries, shared expenses and supplier prices the home currency prices (QA4-D15). */
  pricedRecords: number;
  /** `pricedRecords > 0`, computed server-side so the screen cannot disagree. */
  currencyLocked: boolean;
}

/** Every field optional: the screen saves one section at a time. */
export interface UpdateClinic {
  name?: string;
  defaultTimezone?: string;
  currency?: string;
  /**
   * ISO 3166-1 alpha-2 — the country a bare phone number is read against.
   *
   * **Writable since QA #5/#35.** It was read by patient creation and by
   * patient search, returned by the API, and settable by nothing, so it stayed
   * `null` for every clinic and national numbers could not be entered at all.
   * Empty string clears it, matching the other optional fields here.
   */
  defaultCountry?: string;
  /** Empty string clears it. */
  email?: string;
  /** `HH:MM` — the default working day (ST5). */
  defaultShiftStartsAt?: string;
  defaultShiftEndsAt?: string;
  /** ISO weekdays the practice works, 1 = Monday (QA4-D5). */
  defaultWorkingDays?: number[];
}

/** A logo is drawn, so it is an image — a PDF belongs in a patient's file. */
export const ALLOWED_LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/svg+xml"] as const;
export type AllowedLogoMimeType = (typeof ALLOWED_LOGO_MIME_TYPES)[number];

export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export interface PresignedClinicLogo {
  /** Sent back to confirm, where it is checked rather than trusted. */
  s3Key: string;
  uploadUrl: string;
  expiresInSeconds: number;
}

/**
 * What the sidebar needs, for **every** role.
 *
 * Deliberately narrower than `Clinic`, which is admin-only: the logo is the
 * practice's branding and the whole practice sees it, but the contact block and
 * the priced-record count are not things to hand a hygienist on every page load.
 */
export interface ClinicBranding {
  name: string;
  hasLogo: boolean;
  /** Short-lived and signed. Re-fetched on mount rather than remembered. */
  logoUrl: string | null;
}

// ── Clinic-wide alerts (STORY-089) ──────────────────────────────────────────

/** The three the schema declares, in the order they are ranked. */
export const ALERT_SEVERITIES = ["CRITICAL", "WARNING", "INFO"] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

/**
 * One notice from anywhere in the clinic — the mockup's *Elsewhere in the
 * clinic*.
 *
 * **`severity` decides how the row is drawn; `kind` only labels it.** The
 * mockup coloured by kind because it had no severity column, and a program
 * should branch on a typed enum rather than on free text a writer chose.
 */
export interface ClinicAlert {
  id: string;
  /** Null for a notice about the whole clinic rather than one location. */
  branchId: string | null;
  /**
   * Resolved server-side, so the panel needs no branch list — and a reader is
   * not necessarily rostered to the branch that raised it.
   */
  branchName: string | null;
  kind: string;
  severity: AlertSeverity;
  title: string;
  detail: string | null;
  /** Null draws no chevron, exactly as the mockup draws none without a target. */
  linkTarget: string | null;
  /** A low-stock alert's open task, so Home can say who is on it (OQ-D12); else null. */
  taskId: string | null;
  /** ISO 8601. */
  dueAt: string | null;
}

// ── Home's computed figures (STORY-084) ─────────────────────────────────────

/**
 * Gaps in today's chairs a waiting patient would fit — decision F1.
 *
 * **Zero with an empty waitlist**, and that is the answer rather than a gap in
 * the feature: with nobody waiting there is nobody to put in the gap, which is
 * what the mockup's own note ("waitlist ready") describes.
 */
export interface OpenSlots {
  /** Distinct gaps across every chair, not every quarter hour inside them. */
  count: number;
  /** ISO 8601 — the earliest, for the card's note. Null when there are none. */
  earliestAt: string | null;
}

/**
 * The two figures the browser cannot work out from the day's appointments.
 *
 * *Patients remaining* and *Unconfirmed* are counted client-side from data Home
 * already holds; recounting them server-side would be a round trip for nothing.
 */
export interface HomeFigures {
  openSlots: OpenSlots;
  /** Null for anyone without `billing.view` (STORY-170): what patients owe is billing. */
  paymentsToCollect: PaymentsToCollect | null;
}

/**
 * One week of attendance, for Home's trend chart (Anthony's QA, item 17).
 *
 * Weeks rather than days: a single practice's daily numbers are too small to
 * read as a shape — one no-show on a quiet Tuesday is a 50% miss rate and means
 * nothing.
 */
export interface AttendanceWeek {
  /** Monday of this week, in the branch's own timezone. `yyyy-mm-dd`. */
  weekStart: string;
  attended: number;
  noShow: number;
  cancelled: number;
  /** `noShow ÷ (attended + noShow)`, or **null in a week with neither**. */
  noShowRate: number | null;
}

/** A patient who has been missing appointments, worst first. */
export interface MissedAppointments {
  patientId: string;
  patientName: string;
  patientFileNumber: string;
  noShowCount: number;
  attendedCount: number;
  noShowRate: number;
}

/**
 * How reliably this branch's patients have been turning up.
 *
 * **A cancellation is reported and never counted as a miss** — it is on the
 * chart as its own series because a practice wants to see it, and out of
 * `noShowRate` because cancelling is the patient telling you. The same line K4
 * already draws for the no-show risk flags.
 */
export interface AttendanceTrend {
  from: string;
  to: string;
  attended: number;
  noShow: number;
  cancelled: number;
  noShowRate: number | null;
  /** Oldest week first. Weeks with nothing in them are zeroes, never skipped. */
  weeks: AttendanceWeek[];
  worstAttenders: MissedAppointments[];
}

/**
 * Who owes what on an issued invoice at this location, whatever its date (EB2,
 * superseding F2, which counted only the people in today).
 *
 * **It was a bare count until 2026-08-31.** The card read *"3 · at the desk on
 * exit"* and stopped there, so the desk knew somebody owed something and had to
 * go looking for who and how much. Item 3 of the client's manual QA asked the
 * card to open and say — so the figure carries its own detail rather than the
 * browser making a second, differently-shaped request for it.
 *
 * `outstanding` is **integer minor units per currency, never added across
 * them** (QA4-D6), the location's own currency first; unformatted, because the
 * exponent is the browser's business (QA #69).
 */
export interface PaymentsToCollect {
  /** Distinct patients, not invoices — three unpaid bills is one person to stop. */
  count: number;
  outstanding: MoneyInCurrency[];
  /** Largest balance in the location's own currency first. */
  patients: { patientId: string; name: string; outstanding: MoneyInCurrency[] }[];
}

/**
 * One gap offered for a waiting patient — STORY-086.
 *
 * **The head of a gap, not every quarter hour in it**: the desk is picking a
 * slot, and five consecutive quarter hours read as five options when they are
 * one. A time inside a gap rather than at its start is the calendar's job.
 */
export interface SlotOffer {
  chairId: string;
  /** ISO 8601. */
  startsAt: string;
}

/**
 * One of today's patients whose recent attendance flags them — STORY-088.
 *
 * Two or more misses in their last four appointments, `NO_SHOW` only:
 * **a cancellation is not a miss**, because cancelling is telling you.
 */
export interface NoShowRisk {
  patientId: string;
  missed: number;
  outOf: number;
}

// ── The staff rota (Phase 9) ────────────────────────────────────────────────

/** §6.4's four day types. The program branches on all of them. */
export const STAFF_DAY_KINDS = ["CLINIC", "HALF_DAY", "TRAINING", "OFF"] as const;
export type StaffDayKind = (typeof STAFF_DAY_KINDS)[number];

/** One weekday of a repeating pattern. `HH:MM` on the wire, never a timestamp. */
export interface PatternDay {
  /** ISO-8601: 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  kind: StaffDayKind;
  startsAt: string | null;
  endsAt: string | null;
  /** One unpaid break. §6.4: paid = end − start − break. */
  breakMinutes: number;
}

/**
 * Somebody's repeating week at one branch — STORY-090.
 *
 * **Patterns are versioned** (S1): saving closes the version in force and opens
 * a new one, so an earlier week still reads as it did. `effectiveFrom` is when
 * the version on screen took effect.
 */
export interface WeeklyPattern {
  userId: string;
  name: string;
  /** All seven, Monday first. An absent day is a day off, not a gap. */
  days: PatternDay[];
  paidMinutes: number;
  /** Null when this person has no contracted hours — which is not zero. */
  contractedMinutes: number | null;
  differenceMinutes: number | null;
  effectiveFrom: string | null;
  hasPreviousVersion: boolean;
  /** Other locations they work at (S2). Named here, edited at that branch. */
  elsewhere: string[];
  /**
   * The week to pre-fill the editor with when this person's week was never
   * set (ST3–ST5): the clinic's default day, Monday to Friday. **Nothing is
   * stored** — only Save writes it. Null once any week has been saved.
   */
  suggestedDays: PatternDay[] | null;
}

export interface SavePattern {
  /** `YYYY-MM-DD` — when this version takes effect. */
  effectiveFrom: string;
  days: PatternDay[];
}

/**
 * What the editor can fill a week from — STORY-091, decisions S3 and S4.
 *
 * **Neither source saves.** Every save opens a new version (S1), so a button
 * that wrote straight through would make three clicks three versions with
 * revert able to undo only the last. These fill the unsaved form; Save and
 * Discard stay the only writers, which is also what makes trying one costless.
 */
export interface QuickFillSources {
  /**
   * This location's opening hours, already shaped as a week (S3).
   *
   * **Null when the branch has no hours with times on any weekday** — the fill
   * could then only produce an all-off week, which is never what somebody
   * clicking "use this location's hours" means. The screen says so rather than
   * offering a button that appears to do nothing.
   *
   * The mockup's three literal presets — `09:00–17:00`, `08:00–16:00`,
   * `11:00–19:00` — are scenery and are deliberately not built.
   */
  openingHours: PatternDay[] | null;
  /**
   * Colleagues with a pattern in force **at this branch** (S4).
   *
   * Somebody with hours only at another location is not offered: copying them
   * would blank the editor, which reads as a bug rather than as an answer.
   */
  colleagues: QuickFillColleague[];
}

export interface QuickFillColleague {
  userId: string;
  name: string;
}

/**
 * One dated day of somebody's rota — STORY-092.
 *
 * **The effective day, not the pattern day.** §6.4 has two levels and the
 * exception wins outright, so this carries what is actually true on that date
 * and says which level decided it. The same rule the calendar's availability
 * reads, from the same function — two implementations of "is this person in on
 * Wednesday" is the drift this project spends its comments avoiding.
 */
export interface RotaDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** ISO-8601: 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  kind: StaffDayKind;
  startsAt: string | null;
  endsAt: string | null;
  breakMinutes: number;
  /** True when a one-day exception decided this rather than the weekly pattern. */
  fromException: boolean;
  /** The exception's note, when there is one. Null on a pattern day. */
  note: string | null;
  /**
   * **Where this day is worked**, set by the screen when several locations are
   * shown and a person's weeks are merged (OQ-D17); absent otherwise.
   */
  locationName?: string;
}

/** Seven dated days, plus what they add up to. */
export interface RotaWeek {
  userId: string;
  name: string;
  /** The Monday, `YYYY-MM-DD`. */
  weekStart: string;
  days: RotaDay[];
  /** §6.4: end − start − break, summed across the seven dates. */
  paidMinutes: number;
  contractedMinutes: number | null;
  differenceMinutes: number | null;
  /** How many of the seven are exceptions — what "3 changes" counts. */
  exceptionCount: number;
  /**
   * Whether this branch's week is published, and whether you may see it (S6).
   *
   * **`days` is empty when you may not** — the week is withheld rather than
   * faked, so the screen says it is not published yet instead of drawing an
   * empty rota. An empty rota and an unpublished one are different facts.
   */
  publication: RotaStatus;
}

/** One day overridden. The date is in the path, never the body. */
export interface SaveException {
  kind: StaffDayKind;
  startsAt: string | null;
  endsAt: string | null;
  breakMinutes: number;
  note: string | null;
}

/**
 * Whether one week's rota is published — STORY-095, decision S6.
 *
 * **`visible` is a separate field from `published` on purpose.** They differ
 * for exactly one person — an admin, who drafts the week and so always sees it
 * — and collapsing them would either hide the draft from whoever is writing it
 * or show it to everybody.
 */
export interface RotaStatus {
  /** The Monday, `YYYY-MM-DD`. */
  weekStart: string;
  published: boolean;
  /** When, as an ISO instant. */
  publishedAt: string | null;
  /** Who. Null when they have since left — the person goes, the fact does not. */
  publishedBy: string | null;
  /** Whether the caller may read this week's rota at all. */
  visible: boolean;
}

// ── Time off (STORY-096) ────────────────────────────────────────────────────

/**
 * The four the schema declares. Leave categories are policy rather than
 * software (Q20), and adding a fifth is `ALTER TYPE ADD VALUE`.
 */
export const TIME_OFF_TYPES = ["ANNUAL", "SICK", "UNPAID", "OTHER"] as const;
export type TimeOffTypeName = (typeof TIME_OFF_TYPES)[number];

/** §6.4: pending queue → Approve/Decline → decided, with Reopen. */
export const TIME_OFF_STATUSES = ["PENDING", "APPROVED", "DECLINED"] as const;
export type TimeOffStatusName = (typeof TIME_OFF_STATUSES)[number];

export interface TimeOffRequest {
  id: string;
  userId: string;
  /** Who the leave is for. */
  name: string;
  type: TimeOffTypeName;
  /** `YYYY-MM-DD`, inclusive. */
  startsOn: string;
  endsOn: string;
  /**
   * What it costs against the allowance, in days (S7).
   *
   * **Working days from that person's own rota, half for a half day**, counted
   * across every location they work at — and computed on every read rather than
   * stored, so a pattern that changes later re-counts the request.
   */
  days: number;
  reason: string | null;
  status: TimeOffStatusName;
  /** Who typed it. */
  filedBy: string | null;
  /** True when somebody else typed it — what "logged on behalf" looks like. */
  filedOnBehalf: boolean;
  filedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /**
   * Whether **this caller** may decide it (S8) — pending, and not filed by them.
   * The screen asks rather than guessing, so the button and the endpoint cannot
   * disagree about who is allowed.
   */
  canDecide: boolean;
}

/** Omit `userId` to raise it for yourself; naming somebody else logs it for them. */
export interface RaiseTimeOff {
  userId?: string;
  type: TimeOffTypeName;
  startsOn: string;
  endsOn: string;
  reason?: string;
}

export interface DecideTimeOff {
  decisionNote?: string;
  /**
   * Set once the caller has seen the coverage warning and still means it (S9).
   *
   * **Absent means the warning has not been shown**, so an approval that would
   * leave a date uncovered comes back with the report attached rather than
   * refused — warn before committing, not forbid.
   */
  acknowledge?: boolean;
}

/** One date at one location that would be left with nobody able to see a patient. */
export interface UncoveredDay {
  date: string;
  branch: string;
}

/**
 * What approving a request would leave behind — STORY-097, decision S9.
 *
 * **Two signals answering different questions.** `uncovered` is the rule — a
 * location with no dentist or hygienist rostered is not open. `appointments` is
 * the size of it, and it is the ordinary case: leave approved while somebody
 * still has patients booked happens every week.
 */
export interface Coverage {
  uncovered: UncoveredDay[];
  /** Bookings in the range naming this person. Cancelled and no-shows excluded. */
  appointments: number;
  hasClash: boolean;
  ignoredRequestId: string | null;
}

/**
 * One person's allowance and what has been spent against it — STORY-098, S10.
 *
 * **Computed on every read, never stored.** §6.4 puts it as *taken + approved +
 * pending against the allowance*; only the allowance is a row. A stored balance
 * is a second copy of the same fact, wrong the first time somebody edits a
 * request, reopens a decision or changes a rota.
 *
 * **Annual leave only draws it down** (S10). Sick, unpaid and other are raised
 * and decided like anything else and simply do not consume entitlement.
 */
export interface LeaveBalance {
  userId: string;
  name: string;
  year: number;
  /**
   * Entitlement plus anything carried over.
   *
   * **Null when no allowance row exists** — zero is a real entitlement and its
   * absence is not, so a screen says *not set* rather than *none*.
   */
  entitlementDays: number | null;
  /** Approved annual leave whose dates have passed. */
  takenDays: number;
  /** Approved annual leave still ahead — committed, not yet spent. */
  approvedDays: number;
  /** Waiting on a decision. **Shown beside the rest and not subtracted.** */
  pendingDays: number;
  remainingDays: number | null;
}

/** In, off, or on approved leave — today. */
export const TEAM_STATUSES = ["IN", "OFF", "ON_LEAVE"] as const;
export type TeamStatusName = (typeof TEAM_STATUSES)[number];

/**
 * One person on the roster — STORY-099, decision S11.
 *
 * **Two totals, both labelled.** A pattern is per branch (S2) while a contract
 * is one number for the whole practice, so somebody working at two locations is
 * at 60% on either card alone and 100% across both. `paidMinutes` is this
 * branch's — so a card and the rota tab agree about the person you just looked
 * at — and `clinicPaidMinutes` is every location's, which is what the contract
 * is actually against.
 */
export interface TeamCard {
  userId: string;
  name: string;
  role: string;
  /** This branch's paid minutes — what the load bar draws. */
  paidMinutes: number;
  /** Every location's, summed. What the contract is against. */
  clinicPaidMinutes: number;
  contractedMinutes: number | null;
  /** The other locations they work at, named rather than implied. */
  elsewhere: string[];
  /** Today. **Approved leave outranks the rota**, as the booking picker reads it. */
  todayStatus: TeamStatusName;
}

/* ── Treatment plans ────────────────────────────────────────────────────────
 *
 * **A plan is a time-saver, not a contract** (TP21). It exists so a dentist
 * with ten crown replacements does not write the same steps ten times.
 * Nothing approves one, nothing signs one and nothing versions one — which is
 * why no type here carries an approval, a signature or a version.
 */

/**
 * **Two, not six** (TP24). The database enum keeps four more; they described an
 * approval flow and a versioning scheme that are not built, and naming them
 * here would invite a screen to draw a state nothing can reach.
 */
/**
 * How a step may be moved (TP32): one place up or down inside its visit, or
 * across to another visit entirely.
 */
export type PlanMoveDirection = "UP" | "DOWN";

/** When the invoice is raised (TP9). Both modes produce identical lines. */
export type PlanBillingMode = "PER_PART" | "ON_COMPLETION";

/**
 * **`SCHEDULED` is real**, and was missing until the audit of 2026-09-06.
 *
 * A visit's items become `SCHEDULED` the moment it is booked. The type said
 * otherwise, so nothing on the client could tell a booked visit from a waiting
 * one — which is exactly the question the booking form's plan picker has to
 * ask before offering a visit the server would refuse.
 */
export type PlanItemStatus = "PLANNED" | "SCHEDULED" | "COMPLETED" | "CANCELLED";

/** One step: a catalogue procedure, and the tooth it is done on if it has one. */
export interface PlanItem {
  id: string;
  /**
   * The appointment this step is booked at, or null (decision D6).
   *
   * **How an appointment finds its plan.** The link lives here rather than on
   * the appointment, so the calendar answers "is this part of a plan?" by
   * walking the patient's plans — an asymmetry worth knowing about, and the
   * reason a plan was unreachable from a booking until this was exposed.
   */
  appointmentId: string | null;
  procedureCodeId: string | null;
  label: string | null;
  /** Set where the procedure is per-tooth, null where it is whole-mouth (TP12). */
  fdiNumber: number | null;
  /** Integer minor units, copied from the catalogue when the step was added. */
  priceCents: number | null;
  /** What the catalogue said at that moment. A snapshot, never a live price. */
  cataloguePriceCents: number | null;
  /**
   * **A boolean rather than a comparison** (ADR-0003), so a deliberate override
   * that happens to equal list price is still recorded as a decision.
   */
  priceOverridden: boolean;
  /** Why. Required whenever the price was overridden — the database says so too. */
  overrideNote: string | null;
  /**
   * What the step was done with, where that is a choice (TP13) — the shade, by
   * name. Written when the step is completed, and onto the tooth record beside
   * it so the record still reads once the item is retired.
   */
  shade: string | null;
  status: PlanItemStatus;
  /**
   * Where this step sits in its visit (TP32).
   *
   * Steps used to come back in the order they were created, so correcting a
   * sequence meant deleting both and adding them back the other way round —
   * which threw away the agreed price and the note explaining it.
   */
  sortOrder: number;
  /** When it was done. Null until it is. */
  completedAt: string | null;
}

/**
 * **A part is one visit** — what can be done in the same sitting (TP4).
 *
 * The title is descriptive and optional, because a visit is defined by being
 * one sitting rather than by what it is called. **There is no level above
 * this**: the mockup's Stabilise / Restore / Aesthetic groupings are not built.
 */
export interface PlanPart {
  id: string;
  sortOrder: number;
  title: string | null;
  /**
   * Days after the previous visit this one is usually booked (CH1), set on the
   * plan. A suggestion, never a rule; null on the first visit and wherever
   * nobody set one.
   */
  gapDays: number | null;
  items: PlanItem[];
  /**
   * **Derived, never stored** (TP29): when the last step of this visit was
   * completed, and null while any is outstanding. **An empty visit is never
   * finished** — it is half-built rather than half-done, and calling it
   * complete would let the visit after it be booked before it was written.
   */
  completedAt: string | null;
  /**
   * The appointment this visit is booked at, if it has one that has not fallen
   * through. The link lives on the step (D6), so this is the only thing that
   * lets a visit say **when** it is happening.
   */
  booking: { appointmentId: string; startsAt: string; status: string } | null;
  /**
   * What has been invoiced against this visit's steps, in minor units. **Null
   * for someone without `billing.view`** (QF-D17), as are `invoicedAt` and the
   * plan's `paidCents`.
   */
  invoicedCents: number | null;
  /** What of that is still on a draft invoice (PP-D8). Null without billing.view. */
  draftedCents: number | null;
  /** When the first of those lines was raised. */
  invoicedAt: string | null;
  /**
   * The 1-based number of the earlier visit that has to finish first (TP8), or
   * null when this one can be booked now.
   *
   * **So the screen can say which visit is in the way before somebody starts.**
   * Booking used to be offered on every visit and refused by the server out of
   * turn — deliberately, so the refusal could name the outstanding visit. That
   * was right about the message and wrong about the moment: it arrived after
   * the click, out of a dialog already half filled in.
   */
  blockedByVisit: number | null;
}

export interface TreatmentPlan {
  id: string;
  patientId: string;
  patientName: string;
  /**
   * What the plan is shown under: the typed name, or one built from its steps
   * and teeth each time it is read (TP25, PN1). Null only with neither.
   */
  title: string | null;
  /** What somebody typed as the name, or null — what a rename box pre-fills (PN1). */
  typedTitle: string | null;
  /**
   * The multi-visit procedure this was made from (PV3). **Null means an old,
   * hand-built treatment plan**, kept as read-only history (PV6).
   */
  procedureCodeId: string | null;
  billingMode: PlanBillingMode;
  /**
   * False until the first invoice line is raised, true after (TP30). While it
   * is false the mode can still be changed; after it, the plan has billed one
   * way and switching would leave the rest billing another.
   */
  billingModeLocked: boolean;
  /**
   * **Derived, never stored** (TP29): every visit has steps and every step is
   * done.
   *
   * The `status` column this replaces defaulted to `IN_PROGRESS` and was
   * advanced by nothing — not completing a step, not completing the last one.
   * Every plan ever created read as live, including the finished ones.
   */
  completedAt: string | null;
  /**
   * When this plan was abandoned (TP31).
   *
   * A patient who stops coming leaves a plan that will never complete on its
   * own. Closing bills what was done and takes the rest out of the queue;
   * clearing this reopens it.
   */
  closedAt: string | null;
  notes: string | null;
  /** The visits, in the order they happen. May be empty on a plan just started. */
  parts: PlanPart[];
  /**
   * **Money, computed from the items and never stored** — so it cannot drift
   * from what the plan contains. **Nothing here is a duration** (TP11): the
   * plan does not know how long it takes and does not claim to.
   */
  totalCents: number;
  /**
   * **What has actually been billed**, summed from the invoice lines pointing
   * at this plan's steps, voided invoices excluded.
   *
   * Its absence made the other two unreadable: 1,612 total against 486 paid
   * says nothing about whether the difference is work not yet done, work done
   * and not yet billed, or a bill nobody has settled — and those three need
   * three different people to do three different things. Null without
   * `billing.view` (QF-D17).
   */
  invoicedCents: number | null;
  /** What of that is still on a draft invoice (PP-D8). Null without billing.view. */
  draftedCents: number | null;
  /**
   * **Money received against this plan's invoices** (TP23) — not money billed,
   * which is a different question. **Three figures, not the mockup's four**:
   * insurer share and co-pay are still dropped until there is a real insurance
   * model. Null without `billing.view` (QF-D17).
   */
  paidCents: number | null;
}

/**
 * Still being worked through — **not finished, and not abandoned**.
 *
 * One definition, because three screens ask it and all three used to ask it of
 * a column that never moved: the calendar's plan picker, the completion dialog
 * and the booking form each filtered on `status === "IN_PROGRESS"`, which
 * matched every plan ever created. A patient whose crown was finished last
 * month was still offered it.
 */
export function isPlanOpen(plan: Pick<TreatmentPlan, "completedAt" | "closedAt">): boolean {
  return plan.completedAt === null && plan.closedAt === null;
}

/** Moving a step (TP32). Either a direction or a destination, never both. */
export interface MovePlanItem {
  direction?: PlanMoveDirection;
  /**
   * Another visit of the same plan. The step lands at the end of it and loses
   * whatever appointment its old visit was booked at — it is arriving at a
   * different sitting.
   */
  toPartId?: string;
}

/** Changing how a plan bills, while that is still allowed (TP30). */
export interface SetPlanBillingMode {
  billingMode: PlanBillingMode;
}

/** Start a patient's course of a multi-visit procedure (PV3). */
export interface StartCourse {
  patientId: string;
  procedureCodeId: string;
  /**
   * At least one for a per-tooth procedure, none for a whole-mouth one (TP12).
   * One set of visits covers every tooth named (BF6).
   */
  fdiNumbers?: number[];
  /**
   * The first visit's booking, written with the course in one transaction
   * (PP-D19): a refused slot keeps neither. Its patient is this course's.
   */
  firstVisit?: CreateAppointment;
}

export interface CreatePlanItem {
  procedureCodeId: string;
  fdiNumber?: number;
  priceCentsOverride?: number;
  /** Required whenever `priceCentsOverride` is given. */
  overrideNote?: string;
}

/** Changing a step after the fact. **Never its procedure** — see [[PlanItem]]. */
export interface UpdatePlanItem {
  fdiNumber?: number;
  priceCents?: number;
  overrideNote?: string;
}

export interface CreatePlanPart {
  title?: string;
  items: CreatePlanItem[];
}

export interface CreateTreatmentPlan {
  patientId: string;
  title?: string;
  billingMode?: PlanBillingMode;
  notes?: string;
  /** May be empty — the builder adds visits to a plan that already exists. */
  parts: CreatePlanPart[];
}

/**
 * Marking one step done — STORY-083.
 *
 * **The shade is an inventory item** (TP13), and the candidates are the
 * procedure's own materials: what a composite can be shaded with is what the
 * clinic stocks against one.
 */
export interface CompleteItem {
  shadeItemId?: string;
  /** From M, O, D, B and L. */
  surfaces?: string[];
}

/** Booking one visit into the calendar — STORY-082a. */
/**
 * Where a plan's remaining visits would land — STORY-131b (CH2, CH3). Writes
 * nothing; the booking form shows it, and the person edits it before Book.
 */
export interface ChainProposalRequest {
  chairId: string;
  /** So no proposed time lands on one of the patient's other appointments. */
  patientId?: string;
  /** ISO instant of the first visit — the one the form is booking. */
  startsAt: string;
  durationMinutes: number;
  /** One per remaining visit, in order: days after the visit before. */
  gaps: (number | null)[];
  /** Minutes per remaining visit, when a procedure's visits differ (PV1). */
  durations?: number[];
}

export type ChainUnplaced = "NO_GAP" | "AFTER_UNPLACED" | "NO_FREE_SLOT";

export interface ChainProposal {
  index: number;
  gapDays: number | null;
  /** Where it would go, or null when it could not be placed. */
  startsAt: string | null;
  /** Where the gap alone pointed, before a clash moved it. */
  targetAt: string | null;
  moved: boolean;
  unplaced: ChainUnplaced | null;
}

export interface ChainProposalResult {
  proposals: ChainProposal[];
  searchDays: number;
}

export interface BookChain {
  /** In the plan's order. */
  visits: (BookPart & { partId: string })[];
}

export interface RefusedVisit {
  partId: string;
  startsAt: string;
  reason: string | null;
  message: string;
}

/** **Read both lists** (CH10): some visits may be refused while the rest go in. */
export interface BookChainResult {
  plan: TreatmentPlan;
  booked: string[];
  refused: RefusedVisit[];
}

/** Move one visit and shift the later ones by the same days (CH4). */
export interface ShiftChain {
  appointmentId: string;
  startsAt: string;
  chairId?: string;
}

export interface ShiftChainResult {
  plan: TreatmentPlan;
  /** Appointments moved, the chosen one first. */
  moved: string[];
  /** Later visits left where they were, with why (CH10). */
  refused: { appointmentId: string; targetAt: string; reason: string | null; message: string }[];
}

/** Cancelling the later booked visits of a plan (CH11, CH13). */
export interface CancelLaterResult {
  plan: TreatmentPlan;
  cancelled: string[];
  refused: { appointmentId: string; targetAt: string; reason: string | null; message: string }[];
}

export interface BookPart {
  chairId: string;
  /** ISO instant. */
  startsAt: string;
  /** **Chosen, never computed** (TP11). */
  durationMinutes: number;
  providerId?: string;
}

/**
 * A visit still waiting for a date — STORY-082a.
 *
 * **`READY`** is the next visit and bookable now. **`STALLED`** means the visit
 * in front of it was booked and then cancelled or missed, so the plan waits on a
 * completion that will never come and the earlier visit has to be rebooked.
 * **`AHEAD`** (PP-D9) means a later visit was completed on the calendar first;
 * the row names the earlier visit, and marking it done completes both.
 */
export interface WaitingPart {
  planId: string;
  planTitle: string | null;
  patientId: string;
  patientName: string;
  partId: string;
  partTitle: string | null;
  visitNumber: number;
  visitCount: number;
  reason: "READY" | "STALLED" | "AHEAD";
  /** With `AHEAD`, the later visit already done on the calendar; otherwise null. */
  doneAheadVisit: number | null;
}

/**
 * Google Calendar sync (STORY-045c).
 *
 * **The five states are not interchangeable, and two of them both mean "not
 * syncing"** (decision G23): `AWAITING_CONFIRMATION` is a setup step nobody
 * finished, `BROKEN` is something that worked and stopped. The screen words
 * them differently because the remedies differ — confirm it, versus re-pair it.
 */
export type GoogleSyncStateName =
  "NOT_PAIRED" | "AWAITING_CONFIRMATION" | "ACTIVE" | "BROKEN" | "RETIRED";

export interface GoogleChairStatus {
  chairId: string;
  /** `{Branch} · {Chair}` — the same name the Google calendar carries. */
  label: string;
  state: GoogleSyncStateName;
  calendarId: string | null;
}

export interface GoogleSettings {
  /** The practice's own Google account. Null means sync is off for this clinic. */
  workspaceEmail: string | null;
  /**
   * The service account this clinic's calendars belong to. **Public** — it is
   * what a practice shares an existing calendar with on the fallback path.
   */
  serviceAccountEmail: string | null;
  /** False when the deployment has no service account configured at all. */
  available: boolean;
  chairs: GoogleChairStatus[];
}

/**
 * The kinds of message the product sends — 2026-09-20.
 *
 * **Four, and each is a different answer to "why did this practice get mail".**
 * `STAFF_INVITE` and `CLINIC_INVITE` share one template and are counted apart
 * on purpose: one is an admin adding a colleague, the other is the operator
 * standing a whole practice up, and a console that could not tell them apart
 * would answer the wrong question about where a day's quota went.
 *
 * Stored as text rather than a Postgres enum, so adding a fifth is a line here
 * and not a migration. Nothing branches on the value — it is grouped by, and
 * that is all.
 */
export const MAIL_KINDS = [
  "STAFF_INVITE",
  "CLINIC_INVITE",
  /**
   * **Told, not invited** (OA1). Somebody who already had a login and has been
   * added to another practice gets a notification rather than a link — the
   * membership is already live, so there is nothing to accept. Counted apart
   * from an invitation for that reason: it is mail that cannot be "resent to
   * fix" anything, and a console lumping the two together would suggest it can.
   */
  "PRACTICE_ADDED",
  "PASSWORD_RESET",
  "SIGNUP_WELCOME",
] as const;

export type MailKind = (typeof MAIL_KINDS)[number];

/** How mail is being spent, for the operator console's Health screen. */
export interface MailUsage {
  /** The envelope sender these counts belong to — `SMTP_FROM` as rows recorded it. */
  senders: string[];
  /** Local-day counts, in the server's timezone. */
  today: { sent: number; failed: number };
  last7Days: { sent: number; failed: number };
  /** Today, by message kind. Kinds with no sends are omitted. */
  byKind: { kind: MailKind; sent: number; failed: number }[];
  /** Today, by practice, busiest first. */
  byClinic: { clinicId: string; name: string; slug: string; sent: number }[];
  /**
   * What the relay's daily ceiling is believed to be, from `MAIL_DAILY_LIMIT`.
   *
   * **Advisory, and nothing enforces it** (decided 2026-09-20 — the client
   * chose a gauge over a cap). It moves the bar to amber; it never refuses a
   * send. Null when the deployment has not said what its ceiling is, and the
   * console then shows a count with no bar rather than inventing one.
   */
  dailyLimit: number | null;
  /** The most recent failures, newest first, for when a quota has run out. */
  recentFailures: { kind: MailKind; at: string; failure: string }[];
}

/** A file another one might be (PP-D14) — what the warning and the list name. */
export interface DuplicateCandidate {
  id: string;
  name: string;
  /** Padded, as the server writes it. */
  fileNumber: string;
  dateOfBirth: string | null;
  phoneE164: string;
}

/** What merging one file into another would do (PP-D13), before it does it. */
export interface MergePreview {
  keep: DuplicateCandidate;
  other: DuplicateCandidate;
  /** Rows moving to the kept file, per table. */
  moves: Record<string, number>;
  /** Details that differ, each picked field by field. */
  differing: { field: string; keep: unknown; other: unknown }[];
  /** Teeth removed on only one file, which the administrator settles. */
  removedOnOne: { fdiNumber: number; removedOn: "keep" | "other" }[];
}

/** The administrator's choices for a merge (PP-D13a, PP-D13b). */
export interface MergeChoice {
  details?: Record<string, "keep" | "other">;
  teeth?: Record<string, "removed" | "present">;
}
